// GENERATED by `pnpm run gen:narrative` from src/core/content/narrative/takes/ — DO NOT EDIT.
// Hand edits here are drift by definition: edit the markdown source and regenerate
// (the gen-narrative verify gate rebuilds this file and byte-compares it).
// Data only — the types + helpers stay hand-written in storyTakes.ts, which re-exports this.

import type { StoryTakeBundle } from './storyTakes';
import { NAMES } from '../core/content/names';
import { NPC_NAME } from '../core/content/voices';

export const STORY_TAKE_BUNDLES: readonly StoryTakeBundle[] = [
  {
    id: 'c5a-discoveries',
    title: 'The three hidden discoveries',
    review: 'project/human-in-the-loop/review.md',
    rationale:
      'Take A scored 11✔1✘ — tool-adjacent escalation ("gives like cloth, not like root"; "wider than the badger needs") lands the finds as plain fact with the strangeness visible and unremarked; the one flagged maxim cut per Pass-2.',
    canonLabel: 'A · found by hand',
    rungReason: 'spans R1–R2 (weir find at R1 when travel opens, woodlot/margins at R2)',
    takes: [
      {
        id: 'b',
        label: 'the estate remembers',
        brief: "hints as the past showing through; finds meet a previous keeper's hand",
        scorecard: '10✔ 2✘',
        flavor: {
          reedsHint0:
            'The reeds below the weir keep what the river tires of — a screen-lath gone soft, a sandal sole, losses older than anyone left to claim them.',
          reedsHint1:
            "Some earlier keeper cut ways into this bank for the same work you're at, and the current still uses them, ferrying what it carries into the deepest cut.",
          reedsHint2:
            'The old cut ends at a snag that has caught things since before your time, and it has caught something lately: a pale shape low in the roots, half taken back by the reeds.',
          reedsFound:
            'A bundle, river-swollen, off the same water that brought you. Inside, a paper the river has already read — the ink gone to grey bloom, the folds still sharp from a careful hand.',
          sluiceHint0:
            'The far paddies take their water the long way round, as though the ditches once knew a shorter answer and have forgotten it.',
          sluiceHint1:
            "Where the field-ditch meets the woodlot the grass grows in a line too straight for grass, and under the moss there is dressed stone — someone's good work, going nowhere.",
          sluiceHint2:
            'The dressed channel runs to a gate-post rotted off at the collar, its slot packed hard with silt; when the ditch runs high, the water still leans toward it.',
          sluiceFound:
            "Cleared to the sill, the old gate shows its maker's hand — chisel-dressed cheeks, a hardwood sill set true, work done once and meant to outlast the man who did it. The estate forgot it owned this; the water never did.",
          settHint0:
            'The setts at the field margin are new digging over old — the badgers have held this ground longer than the field has been a field.',
          settHint1:
            'Driving them out, you find the spoil ages with the holes: fresh earth at the young ones, and at the grandfather sett, grey rubble that never came out of any field.',
          settHint2:
            'The grandfather sett is dug through made ground — roof-tile, a squared footing stone — and its spoil runs in a line that ends where the waste ground begins.',
          settFound:
            "The main run passes beneath the old wall's footing, through a gap left when the stone was first laid — a badger's road, worn smooth by generations. The rope stands and the warning stands; the badgers were simply never told.",
        },
      },
      {
        id: 'c',
        label: 'nobody says',
        brief: 'hints ride silences and avoidances',
        scorecard: '9✔ 3✘',
        flavor: {
          reedsHint0:
            'The cutters have taken reed from every stand along the weir but one, downstream, left whole for no reason anyone offers.',
          reedsHint1:
            'Matsuzō, who counts every gnawed screen aloud, goes quiet by the downstream stand and finds his count again past it.',
          reedsHint2:
            'The untouched stand is the stretch where the river gave you up; no one has cut there since, and no one has said so.',
          reedsFound:
            'Deep in the roots, a bundle: cloth gone river-grey, tied off in a knot your own hands could have made, and inside it a paper the water has finished — the ink walked off into grey, not one word left standing. You bind it up the way it was, and say nothing either.',
          sluiceHint0:
            "The field-talk follows the water's whole length, complaint by complaint, and always dries up at the same place — a little short of the woodlot.",
          sluiceHint1:
            'An old straight cut runs from the paddies in under the trees, and the mowing has bent around it for years — not one scythe-mark crosses it.',
          sluiceHint2:
            'Ganzō has a year for every flood and every snow, but the cut at the woodlot edge gets no year from him nor from anyone — the one question at the well that turns into talk of weather.',
          sluiceFound:
            "Under the silt, timber: a sluice gate, hand-squared and still sound, choked to the lintel — a whole gate's worth of water the paddies have thirsted past for years. It stands in no ledger, and nobody's talk has ever carried it.",
          settHint0:
            'The drivers work every burrow along the margin but the oldest one, nearest the waste ground, which the older men pass without seeming to see.',
          settHint1:
            "Every sett on the margin wears some man's mark — a snare-peg, a spade-lip — except the old diggings that run toward the wall, where no mark has ever been left.",
          settHint2:
            'The badger runs all gather toward the foot of the ruined wall, and whoever drove these fields before you stopped their digging short of it every time, at the same line, with no word left as to why.',
          settFound:
            "The oldest gallery does not stop: it goes down beneath the wall's footing and keeps on, smooth-worn earth, dark past the reach of an arm. Overhead the rope says what it has always said; underfoot the ground has been saying otherwise for a long time.",
        },
      },
    ],
  },

  {
    id: 'c5a-judge',
    title: 'The per-grade day-book judge lines',
    review: 'project/human-in-the-loop/review.md',
    rationale:
      'Takes B and C tied 5✔1✘; C wins on TST3 — koku standing IS the valley\'s regard, so the outside-eyes register is the mechanic\'s own fiction ("without the pause that used to follow it"); the counted-twice echo redlined per Pass-2.',
    canonLabel: 'C · as the valley sees it',
    rung: 7,
    takes: [
      {
        id: 'a',
        label: "the board's arithmetic",
        brief: 'each grade as what the columns show',
        scorecard: '4✔ 2✘',
        flavor: {
          judgeLineFail:
            'The season enters the book as loss: what went out takes more of the page than what came in, and no entry is made smaller for being shameful. Exactness is the one thing the house is not yet poor in.',
          judgeLineBad:
            'The columns close short, and the book says so in the same hand it says everything else. What the season would not give, the page does not pretend to hold.',
          judgeLineOk:
            'Taken against given, the season stands level — nothing owed forward, nothing to carry. The book closes even, which is not yet the same as closing well.',
          judgeLineGood:
            'The in-column outlasts the out-column, and a remainder passes to the season ahead. It is a small thing, written no larger than anything else on the page.',
          judgeLineGreat:
            'Every heading the book keeps ends the season fuller than it began. The surplus is ruled off with the same care once spent on losses, as if plenty too must answer for itself.',
          judgeLineExcellent:
            'No column ends the season worse, and several end it fuller than the book remembers. Read together, the lines amount to a house on its own feet again — though the book leaves that word to whoever reads it.',
        },
      },
      {
        id: 'b',
        label: 'the house as body',
        brief: "each grade read off the estate's physical bearing",
        scorecard: '5✔ 1✘',
        flavor: {
          judgeLineFail:
            'The season closes on a house going down under its own weight: more came off the roofs than any hand put back, and the hands cross the court quickly, the way people cross a sickroom.',
          judgeLineBad:
            'The house held, the way a man holds who cannot afford to fall — the failed timber propped, not replaced, and the props are what a passing eye finds first.',
          judgeLineOk:
            'The season leaves the house as it found it: nothing lost, nothing mended past its need. The gate opens on the same groan it opened on in the first week.',
          judgeLineGood:
            "Something in the house's bearing has straightened — a roof-line true where it sagged — and the hands take the court at a walk now, because the day has room in it again.",
          judgeLineGreat:
            'The house moves like a body past its fever: repairs made before they were wanted, and the sound of the yard is work chosen, not work owed.',
          judgeLineExcellent:
            "The house stands the way it must once have stood — square at the gate, dry under every roof, the court crossed at a working man's easy pace — and the valley, passing, sees it.",
        },
      },
    ],
  },

  {
    id: 'c5a-nodes-autumn',
    title: 'The autumn node reads (16 zones)',
    review: 'project/human-in-the-loop/review.md',
    rationale:
      'Take B (senses-first) had the strongest Pass-2 verdict; the flagged anchor-span duplications were redlined (de-verbatimed or cut) before landing.',
    canonLabel: 'B · senses-first',
    rung: 2,
    takes: [
      {
        id: 'a',
        label: 'work-first',
        brief: 'each node read through what its labour or duty IS this season',
        scorecard: '8✔ 8✘',
        flavor: {
          nodeWeirRiverbankBlurbAutumn:
            "Matsuzō's weir, leased; with the paddies cut dry the water has done its year's work, and the screens come up one at a time for mending before the cold gets into the river. This is where they pulled you out.",
          nodeWeirReedsBlurbAutumn:
            'The shallows above and below the weir, and the reeds are at their cutting height — thatch, matting, rack-ties, taken bundle by bundle while the water is still warm enough to stand in. The rats do not pause for the season, and every screen they cost is still coin owed.',
          nodeGateGateyardBlurbAutumn:
            "The estate's face, kept barely, and autumn is the one season it earns its width: carts in from the fields, straw and sacks over Yohei's boards on market days. At the season's end, what the house owes goes out through these posts too.",
          nodeForecourtBlurbAutumn:
            "The outer court of the guest house, given over to the harvest's second half: sheaves beaten out, grain winnowed and sacked in the open, chaff in everything. Genemon's window looks onto all of it, and the writing keeps pace with the work.",
          nodeWoodshedBlurbAutumn:
            "A lean space between the woodpiles — mat, bowl, nail — and the piles are growing now, the winter's wood coming in split and stacked on every side of it. The room gets smaller by the week; what is left of it is still yours.",
          nodeKitchenThresholdBlurbAutumn:
            "Meals are taken at the threshold, and in cutting season they are brought out to it — rice and pickle at whatever hour the field lets go of you. The new rice is within, being weighed; what is eaten at the sill is still last year's.",
          nodeShrineCorridorBlurbAutumn:
            'A family altar set into a corridor, and the first of the new rice goes there before it goes anywhere — a small bowl, set and changed without ceremony. You see it in passing; the duty is not yours to keep.',
          nodeKuraBlurbAutumn:
            "The working storehouse in its one great season: the harvest comes through the iron-strapped door sack by sack, and the stool beside it is not left empty now, even at meals. What stands inside by autumn's last day must feed the house and pay for it too.",
          nodeSickroomBlurbAutumn:
            'A lean-to surgery off the outer court: herbs, a pallet, the smell of moxa — and in cutting season, sickle-opened hands and backs that gave at the third field. You woke here first; this time of year the pallet is seldom cold.',
          nodeDrillYardBlurbAutumn:
            "The old stable court, Kihei's ground, and in autumn he stands most of the drill down — every arm that can swing a sickle is owed to the fields first. The yard waits, swept, for the season to be over.",
          nodeHomePaddyBlurbAutumn:
            "The guest house's skirts, and the whole year bends down to them: the rice comes off row by row while the weather allows it, mud to the knee, sickles going from first light. What the house owes at the season's end will come out of this mud and no other.",
          nodeFieldMarginsBlurbAutumn:
            "Setts at the paddy's edge — tanuki, badger — and the drying racks beside them standing full, which makes autumn their season as much as the house's. Watching what is cut is work now in its own right, and the fat animal at the racks knows the watch cannot be everywhere at once.",
          nodeWoodlotEdgeBlurbAutumn:
            "Kindling and forage country past the last fence, and its one demanding season is now: the winter's wood is cut and hauled while the ground is dry and the days still have length in them. No one works out here beside you, and the pile grows only as fast as your own arms.",
          nodeRuinedCompoundBlurbAutumn:
            'Beyond a rope and a written warning: fallen roofs, a gate down across its own step — and the one ground on the estate that autumn asks nothing of. Nothing is cut here, nothing carried, nothing weighed; the house calls it the waste ground and leaves it out of the year entirely.',
          nodeOvergrownOrchardBlurbAutumn:
            "The old orchard gone wild, and in autumn it still fruits — a harvest no one gathers, dropping into the bramble for the dogs and the wasps. It is the only crop on the estate that comes in without a single hand's work, and the house lets every piece of it rot.",
          nodeBambooGroveBlurbAutumn:
            "Green dark and creaking behind the waste ground; autumn sends you in with a saw, for rack-poles and flails and the year's mending all want cut bamboo. The monkeys work the vegetable rows harder now — they also know what a harvest is.",
        },
      },
      {
        id: 'c',
        label: 'people-first',
        brief:
          'each node read through its seasonal traffic — who crosses it, what is carried through it',
        scorecard: '8✔ 8✘',
        flavor: {
          nodeWeirRiverbankBlurbAutumn:
            "Matsuzō's weir, leased, its screens the house's to keep whole; at cutting time the bank below it turns working road — sickles rinsed in the shallows, sheaves handed along above the sill. The same water they pulled you out of, running low now, and busy.",
          nodeWeirReedsBlurbAutumn:
            "The shallows above and below the weir, the reeds past the waist and gone to seed; cutters work Matsuzō's bank for thatch while the dry days hold. On the house's side the gnawed screens wait unmended until the harvest can spare a hand.",
          nodeGateGateyardBlurbAutumn:
            "The estate's face, and in autumn its busiest door: borrowed carts through the posts, porters bent under the harvest, Yohei's stall inside the gateyard doing more listening than selling. The ground still gets swept, later in the day than usual.",
          nodeForecourtBlurbAutumn:
            "The outer court of the guest house, surrendered for these weeks to the harvest — grain drying on mats where the day's work is usually set out, turned by whoever crosses with a free hand. Genemon's window stands open on all of it, and the writing keeps pace.",
          nodeWoodshedBlurbAutumn:
            "A lean space between the woodpiles — the mat, the bowl, the nail for your coat — and the piles rising daily now, winter's wood coming in on every idle back. The men stacking work around your corner and leave the nail alone.",
          nodeKitchenThresholdBlurbAutumn:
            'Meals are taken at the threshold, on the outer side of the sill, and in autumn the bowls come fuller and faster — the kitchen feeds cutters in shifts. Within, at the board, the talk has gone all to what the year owes, carried on over you as ever.',
          nodeShrineCorridorBlurbAutumn:
            'A family altar set into a corridor, the rites kept properly in a passage never meant for them; since the cutting ended a small bowl of the new rice sits among them, renewed each morning before the house is up. You pass it oftener now, always carrying something.',
          nodeKuraBlurbAutumn:
            "The working storehouse in its one great season: the iron-strapped door open more than shut, porters through it all day, Genemon at the jamb with the book. The grain-watch's stool is manned every night now, and Kihei walks past it twice to see that it is.",
          nodeSickroomBlurbAutumn:
            "The lean-to surgery off the outer court, where you first woke; at cutting time Sōan's trade is sickle-work — opened hands, a turned heel — and the herb drawers stand open half the day. Nobody is let lie on the pallet long, because the fields cannot spare them.",
          nodeDrillYardBlurbAutumn:
            "The old stable court, Kihei's ground, half-lent to the harvest — drying racks along the stall wall, straw in the corners, his drills moved to the early dark when the hands come in from the fields. Lent, he says, not given.",
          nodeHomePaddyBlurbAutumn:
            "The paddies at the guest house's skirts, and for these weeks the center of everything: every back the estate can raise is bent in the rows, cutting while the weather allows. Even the household comes down to it — the kitchen sends the noon meal out to the field edge rather than lose the walk.",
          nodeFieldMarginsBlurbAutumn:
            "Setts at the paddy's edge, and above them the drying racks heavy for the first time all year; the tanuki works nights now, and so does whoever Kihei can spare to sit against it. The seed store is looked at first thing each morning.",
          nodeWoodlotEdgeBlurbAutumn:
            "Kindling and forage country past the last fence, and autumn is its hauling season — winter's wood comes out of here load by load, most of it on your back. The valley's cutting reaches this far as a thin sound, and deeper in, as nothing.",
          nodeRuinedCompoundBlurbAutumn:
            'Beyond the rope, the fallen roofs and quarried walls the house calls the waste ground. In a season that finds a use for every hand on the estate, none is ever sent past the rope, and nobody remarks on it.',
          nodeOvergrownOrchardBlurbAutumn:
            'The old orchard gone wild, and in autumn its trees still keep their old habit: fruit nobody picks, dropping into the bramble. No one is spared to gather it — the dogs do the gathering here, and come out of the hollow sleek while the season lasts.',
          nodeBambooGroveBlurbAutumn:
            "Green dark and creaking, close behind the waste ground, and at harvest the monkey troop keeps the household's hours — the racks and the rows by day, up the poles by dusk. Shinnosuke, forbidden the grove, has been seen carrying stones in: his own idea of a harvest watch.",
        },
      },
    ],
  },

  {
    id: 'c5a-nodes-bon',
    title: 'The bon node reads (16 zones)',
    review: 'project/human-in-the-loop/review.md',
    rationale:
      'Take B (senses-first) had the strongest Pass-2 verdict; the flagged anchor-span duplications were redlined (de-verbatimed or cut) before landing.',
    canonLabel: 'B · senses-first',
    rung: 2,
    takes: [
      {
        id: 'a',
        label: 'work-first',
        brief: 'each node read through what its labour or duty IS this season',
        scorecard: '11✔ 5✘',
        flavor: {
          nodeWeirRiverbankBlurbBon:
            "Matsuzō's leased weir, its screens the house's to keep whole — and in Bon week the lanterns set on the water upstream come down in the dark and catch against them. The morning's first work is lifting each one over the sill, still lit where it can be, and setting it back on the current.",
          nodeWeirReedsBlurbBon:
            'The reed shallows above and below the weir, where the rats work the leased screens. Matsuzō keeps Bon at his own hearth and counts nothing that week; the rats keep no week at all, and the damage waits for him, fatter.',
          nodeGateGateyardBlurbBon:
            "The estate's face, swept as ever, though Bon week no stall sets up inside the posts. The road past the gate carries the whole valley home to itself, and the gate's work is watching other people's traffic go by.",
          nodeForecourtBlurbBon:
            "The outer court of the guest house, where the day's work is set out — and in Bon week the setting-out is brief, half the estate's tasks stood down for the season. What little is done is still done under Genemon's window, and still written.",
          nodeWoodshedBlurbBon:
            'Your mat and bowl between the woodpiles. Bon sends every hired hand back to his own village for the week; the shed keeps the one man with no village to be sent to.',
          nodeKitchenThresholdBlurbBon:
            "Meals still come to the sill, taken on the outer side as always. In Bon week the kitchen's hardest cooking is for eaters not at the board — the dead take their dishes first, morning and evening, before the living are served.",
          nodeShrineCorridorBlurbBon:
            "The altar in its corridor, in the one week of the year that fills it: the shelf dressed in green, small dishes carried in and out, the dead fed as regularly as the household. The duty is the family's, and none of it is handed to you.",
          nodeKuraBlurbBon:
            'The working storehouse, its good lock keeping Bon like any other week. Rice for the altar dishes goes out against the book, and the stool by the door stays manned through the holiday — the roads are full, and not everyone on them is going home.',
          nodeSickroomBlurbBon:
            "Sōan's lean-to surgery off the outer court. Bon fills the roads, and the roads fill his bench — road-sore feet, travellers' fevers — so the drawers work harder this week than in any other.",
          nodeDrillYardBlurbBon:
            "The old stable court, Kihei's ground, and Bon stands nothing down here. The roads are full of strangers for the week, so the rounds double, and the yard drills while the rest of the valley dances.",
          nodeHomePaddyBlurbBon:
            'The paddy and rows that pay for everything else, in the one week they ask least: the rice stands in ear, the weeding is behind, and the work is water — walk the channels, mind the levels, and let the grain get on with it.',
          nodeFieldMarginsBlurbBon:
            "The setts and runs at the paddy's edge, tanuki and badger country. An offering set out for the dead is food set out, as far as the animals read it, and Bon week adds the altar dishes to everything at the margins that already wants watching.",
          nodeWoodlotEdgeBlurbBon:
            "Kindling and forage country past the last fence, with Bon's own errand in it: pitch-pine and dry brush for the welcome fires, cut where the sound of the estate has already stopped.",
          nodeRuinedCompoundBlurbBon:
            'Beyond the rope: the fallen roofs and quarried walls the house calls the waste ground. In the week when a lamp is set at every door in the valley, lived-in or not, no one carries a light past the rope, and no work of the season crosses it either.',
          nodeOvergrownOrchardBlurbBon:
            'The old orchard gone to bramble, the dogs denned in its hollow. Bon feeds more than the dead — the dogs work the village altars by night for what is left out, and come back to the hollow bolder for it.',
          nodeBambooGroveBlurbBon:
            'The green dark behind the waste ground, monkeys in the stems. The one week the house sends a blade in on purpose is this one — the altar shelf wants fresh green bamboo, and the cutting is done quick, counted poles, no lingering.',
        },
      },
      {
        id: 'c',
        label: 'people-first',
        brief:
          'each node read through its seasonal traffic — who crosses it, what is carried through it',
        scorecard: '6✔ 10✘',
        flavor: {
          nodeWeirRiverbankBlurbBon:
            "Matsuzō's leased weir, the river loud over its sill — the water they pulled you out of. Through Bon the village's lanterns come down the current at dusk, and Matsuzō keeps his bank to see each one over the sill and away.",
          nodeWeirReedsBlurbBon:
            "The shallows above and below the weir, reeds past the waist, the house's leased screens standing in them. In Bon week the reeds catch lanterns as readily as anything else the river carries, and O-Ume wades in at dark to free the ones that snag — hers or anyone's.",
          nodeGateGateyardBlurbBon:
            "The estate's face, kept as barely as ever: sound posts, swept ground. No stall inside them this week — the road beyond is the traffic, everyone in the valley walking home — and the monk Iori takes his corner at the posts, greeting travellers as if already seeing them off.",
          nodeForecourtBlurbBon:
            "The outer court of the guest house, where the day's work is set out under Genemon's window. Through Bon what crosses it is bound for the alcove, not the day-book, and the window has little to write.",
          nodeWoodshedBlurbBon:
            "Between the woodpiles, a mat and a chipped bowl and a nail for the coat. Bon empties the valley toward its family altars; the one visit the woodshed gets is O-Hisa's mending, left on the step without a knock.",
          nodeKitchenThresholdBlurbBon:
            'Meals are taken at the threshold, on the outer side of the sill. In Bon week the kitchen cooks for more mouths than the house has living, and no one at the board remarks on it.',
          nodeShrineCorridorBlurbBon:
            'The family altar in its corridor, rites kept properly in a passage never meant for them. At Bon the whole household files through in order, Toku first, each with something small to carry; you are not in the order.',
          nodeKuraBlurbBon:
            'The working storehouse behind its iron-strapped door — rice counted in, rice counted out. Bon empties the yards to the rites, but the stool beside the door is never empty; the grain-watch is the one duty the season does not excuse.',
          nodeSickroomBlurbBon:
            'The lean-to surgery off the outer court — herb drawers, a pallet, the smell of moxa — where you first woke. Bon fills the road, and the road keeps Sōan at his drawers: blistered feet, a child feverish from the walk home.',
          nodeDrillYardBlurbBon:
            "The old stable court, swept bare — Kihei's ground, run to his order. He calls no drill in Bon week and is in the yard anyway, raking ground that is already clean.",
          nodeHomePaddyBlurbBon:
            "Paddy water and vegetable rows, the plain work that pays for everything else on the estate. Bon sends the hired hands home to their own villages' dead; the water still wants minding, and the minding falls to whoever has nowhere to walk to.",
          nodeFieldMarginsBlurbBon:
            "Setts at the paddy's edge — tanuki, badger — an easy trot from the drying racks. Bon takes the watchers off to the rites and the road, and the setts know a slack week when they smell one.",
          nodeWoodlotEdgeBlurbBon:
            'Kindling and forage country past the last fence, worked by no one but you. The welcome-fires at every door this week burn what came out of here on your back.',
          nodeRuinedCompoundBlurbBon:
            'Fallen roofs and a downed gate beyond the rope and its written warning — the waste ground, the house calls it. In a week when the whole valley lights lanterns for its dead, nothing beyond the rope gets one.',
          nodeOvergrownOrchardBlurbBon:
            'The old orchard gone wild: bramble to the shoulder, the dogs denned in the hollow. Bon sends O-Yae to its edges with a basket, after windfall sound enough for the altar; she keeps to the first rows, and the dogs let the arrangement stand.',
          nodeBambooGroveBlurbBon:
            "Green dark and creaking behind the waste ground, the monkey troop's road to the vegetable rows. Bon is their best week of the year — the rites hold the household indoors, and even Shinnosuke, forbidden the grove, is stood in the alcove line instead.",
        },
      },
    ],
  },

  {
    id: 'c5a-nodes-newyear',
    title: 'The newyear node reads (16 zones)',
    review: 'project/human-in-the-loop/review.md',
    rationale:
      'Take B (senses-first) had the strongest Pass-2 verdict; the flagged anchor-span duplications were redlined (de-verbatimed or cut) before landing.',
    canonLabel: 'B · senses-first',
    rung: 2,
    takes: [
      {
        id: 'a',
        label: 'work-first',
        brief: 'each node read through what its labour or duty IS this season',
        scorecard: '5✔ 11✘',
        flavor: {
          nodeWeirRiverbankBlurbNewyear:
            "Matsuzō's weir, leased, where they pulled you out of the river; the screens across it are the house's to keep whole, and the lease turns with the year. So the season's first work is proving them sound — walked slat by slat before the new book takes any other line.",
          nodeWeirReedsBlurbNewyear:
            "The shallows above and below the weir, the reeds standing winter-dead and rattling. Dead reed is cutting reed — the season's one crop here — and the rats gnaw the leased screens straight through the holiday, at their own steady rate.",
          nodeGateGateyardBlurbNewyear:
            "The estate's face, kept barely — except now, when the village goes visiting and every gate is looked at. The sweeping starts before light and finishes finer than any other week's, and nobody has to be told.",
          nodeForecourtBlurbNewyear:
            "The outer court of the guest house, where the day's work is set out and seen to. In the year's first days Genemon's window is lit early over the opened accounts, and the work stands in the court waiting to be written before it is done.",
          nodeWoodshedBlurbNewyear:
            "A lean space between the woodpiles: a mat, a bowl, a nail for the coat. The piles are the winter's wages going out — what autumn stacked, the new year burns — and your room grows by an armload a day.",
          nodeKitchenThresholdBlurbNewyear:
            "Meals are taken at the threshold, on the outer side of the sill. The kitchen did the new year's cooking in the old year — the fire sits low, the food comes made, and for a few days the loudest work within is the talk over you.",
          nodeShrineCorridorBlurbNewyear:
            "A family altar set into a corridor, and this is the week the corridor earns it: first water, first rice, the year's opening rites kept properly in a passage never meant to hold them. You pass more duty than usual, and are still not asked to stop.",
          nodeKuraBlurbNewyear:
            "The working storehouse: rice in, counted; rice out, counted again. The year opens on a full count — everything behind the iron-strapped door tallied to the book's first page, the grain-watch's stool empty while the door stands open for the counting.",
          nodeSickroomBlurbNewyear:
            "A lean-to surgery off the outer court: drawers of herbs, a pallet, the smell of moxa; you woke here first. The new year sends Sōan its winter trade — coughs, chilblains, a village chest gone bad — and he keeps no holiday his patients don't.",
          nodeDrillYardBlurbNewyear:
            "The old stable court, swept bare, run in Kihei's order or not at all. He allows the new year one idle morning; after that the year's first drill goes up cold and early, and absence is noticed.",
          nodeHomePaddyBlurbNewyear:
            "The guest house's skirts: paddy, vegetable rows, the plainest work there is — except now, when the fields ask nothing. The new year's field-work is done sitting down: hafts rewedged, blades taken to the stone, rope twisted against a spring that will want all of it.",
          nodeFieldMarginsBlurbNewyear:
            "Setts at the paddy's edge — tanuki, badger — and this deep in the cold the seed store is the only table still laid. Standing between it and them is the one field duty the season does not suspend.",
          nodeWoodlotEdgeBlurbNewyear:
            'Kindling and forage country past the last fence, and the one work the new year raises instead of resting: every fire the house keeps lit this week is fed from here. A hundred paces in, the holiday stops with the rest of the sound.',
          nodeRuinedCompoundBlurbNewyear:
            'Beyond a rope and a written warning: roofs fallen in on themselves, a great gate down across its own step — the house calls it the waste ground. The new year, which finds work for every corner of the estate, sends nobody here; no pine, no sweeping, no rite, and no one asks why not.',
          nodeOvergrownOrchardBlurbNewyear:
            "The old orchard gone wild: bramble to the shoulder, the dogs denned in the hollow. Midwinter is their working season — lean, bold, abroad in daylight now — and the house's only labour here is the counting of what they take.",
          nodeBambooGroveBlurbNewyear:
            "Green dark and creaking, close behind the waste ground. The grove owes the new year one errand — clean stems cut for the gate's dressing, done quick and in company while the monkeys watch from the high green; Shinnosuke is forbidden to help, and helps.",
        },
      },
      {
        id: 'c',
        label: 'people-first',
        brief:
          'each node read through its seasonal traffic — who crosses it, what is carried through it',
        scorecard: '2✔ 14✘',
        flavor: {
          nodeWeirRiverbankBlurbNewyear:
            "Matsuzō's weir, leased, the screens the house's to keep whole — the water you were pulled from. At the year's turn the house's first errand comes down this bank: the lease settled into Matsuzō's hands, who takes it, says the water is low, and says nothing else.",
          nodeWeirReedsBlurbNewyear:
            'The shallows above and below the weir, the reeds dead and standing past the waist. The first screen-count of the year is walked through frost-stiff mud, and it is you who walks it; Matsuzō raises a hand from his own bank, which from him is the whole greeting.',
          nodeGateGateyardBlurbNewyear:
            "The estate's face: sound gateposts, bare of paint, swept ground — wearing its small pair of pines this week. The year's turn brings the gate its traffic: village men up to the posts with the season's greeting, Iori the traveling monk at his lodging inside them, Yohei's stall back with the first market.",
          nodeForecourtBlurbNewyear:
            "The outer court of the guest house, where the day's work is set out under Genemon's window. On the first working morning of the year the hands stand in a row on the swept ground to hear the orders, and the day-book they are entered in has nothing written in it yet.",
          nodeWoodshedBlurbNewyear:
            "A lean space between the woodpiles — a mat, a bowl, a nail — and yours. On the year's first morning something waits on the doorstep: a mended coat, folded, with a straw twist of rice-cakes on top, left by whoever leaves such things without knocking.",
          nodeKitchenThresholdBlurbNewyear:
            "Meals are taken at the threshold, on the outer side of the sill. In the year's first days the sill is the busiest border on the estate — O-Yae carrying the village's greetings in and the house's back out — and your bowl comes across it with a share of the festival rice, unremarked.",
          nodeShrineCorridorBlurbNewyear:
            "A family altar set into a corridor, seen in passing. At the year's turn Toku is at it longer than the passage allows for, setting the new offerings straight with both hands, and the household learns to go around while she does.",
          nodeKuraBlurbNewyear:
            "The working storehouse: rice in and out by count, behind the iron-strapped door. The year opens here before it opens anywhere — Genemon at the door with the new book, the grain-watch waiting off its stool, the whole store counted against a first page that must agree with the old year's last.",
          nodeSickroomBlurbNewyear:
            "A lean-to surgery off the outer court — drawers of herbs, a pallet, moxa; you woke here first. The cold brings Sōan the village's winter trade in the year's first days, coughs and chilblains, and each caller leaves some small thing on the step that was not asked for.",
          nodeDrillYardBlurbNewyear:
            "The old stable court, swept bare and given to Kihei's use. The village keeps its holiday; the yard keeps its hour — the year's first drill runs the same as the last one, with Shinnosuke on the wall, loose from his lessons and watching.",
          nodeHomePaddyBlurbNewyear:
            "The guest house's skirts — paddy water and vegetable rows, the work everything else is paid from. At the year's turn the hands are away to their own villages, and for those few days the frost across the stubble carries one line of footprints, yours.",
          nodeFieldMarginsBlurbNewyear:
            "Setts at the paddy's edge — tanuki, badger — within raiding reach of the racks and the seed store. While the household keeps its feast days the animals keep theirs, and the one human track worn through the frost along the margin is O-Ume's, going out and back as she always does.",
          nodeWoodlotEdgeBlurbNewyear:
            'Kindling and forage country past the last fence, worked by nobody but you. The new year comes out this far as sound only — a little drumming from the village on the still mornings — and nobody comes with it.',
          nodeRuinedCompoundBlurbNewyear:
            "Beyond the rope and the written warning: fallen roofs, a great gate down across its own step, walls quarried low — what the house calls the waste ground. Gates all down the valley have their pine up this week; this one gets none, and nobody's errand carries any past the rope.",
          nodeOvergrownOrchardBlurbNewyear:
            "The old orchard gone wild — bramble to the shoulder, windfall, the dogs denned in the hollow. The feast days' scraps on the kitchen midden have been teaching them the way to the house, and Kihei has begun naming them in the morning orders, which he does for nothing harmless.",
          nodeBambooGroveBlurbNewyear:
            "Green dark and creaking behind the waste ground — the one thing still green in the valley's winter. The monkeys sit high in the stems watching the feast-day smoke off the kitchen, and Shinnosuke, whose holiday freedom was never meant to reach the grove, is in the grove.",
        },
      },
    ],
  },

  {
    id: 'c5a-nodes-spring',
    title: 'The spring node reads (16 zones)',
    review: 'project/human-in-the-loop/review.md',
    rationale:
      'Take B (senses-first) had the strongest Pass-2 verdict; the flagged anchor-span duplications were redlined (de-verbatimed or cut) before landing.',
    canonLabel: 'B · senses-first',
    rung: 2,
    takes: [
      {
        id: 'a',
        label: 'work-first',
        brief: 'each node read through what its labour or duty IS this season',
        scorecard: '13✔ 3✘',
        flavor: {
          nodeWeirRiverbankBlurbSpring:
            "Matsuzō's weir, leased; the screens are the house's to keep whole, and the thaw makes that a daily labour — every stick the flood carries down must come off them before the next arrives. This is where they pulled you out, and the water stands higher now than the day they did.",
          nodeWeirReedsBlurbSpring:
            "The shallows above and below the weir, the reeds bent flat under thaw-water. The rats' winter work on the screens cannot be counted until the river drops; Matsuzō waits on his own bank, and the house waits on Matsuzō.",
          nodeGateGateyardBlurbSpring:
            "The estate's face, kept barely: gateposts sound, the ground swept — though in planting weeks the sweeping loses to the mud, every load for the paddies going out between the posts. Yohei's stall still sets up inside them on market days, between the wheel-ruts.",
          nodeForecourtBlurbSpring:
            "The outer court of the guest house, where the day's work is set out — and no season sets out more of it: tools, seed, the order of the planting, assigned before light. Genemon's window looks onto it; nothing goes to the fields unwritten.",
          nodeWoodshedBlurbSpring:
            "A lean space between the woodpiles: a mat, a chipped bowl, a nail for the coat. The piles stand winter-low and are nobody's work until autumn — the one corner of the estate the season asks nothing of.",
          nodeKitchenThresholdBlurbSpring:
            'Meals are taken at the threshold, on the outer side of the sill — earlier now, and eaten faster, planting hours being what they are. Within is the board, where portions are decided; with the seed grain gone to the beds, the deciding runs longer than it did.',
          nodeShrineCorridorBlurbSpring:
            'A family altar set into a corridor, the rites kept properly in a passage never meant to hold them — and kept fuller now, the planting having observances of its own. You see them in passing; you are not asked to stop.',
          nodeKuraBlurbSpring:
            'The working storehouse in its emptying season: what goes over the sill now is seed grain, weighed out against a harvest that does not yet exist. The grain-watch keeps the stool by the iron-strapped door all the same.',
          nodeSickroomBlurbSpring:
            'A lean-to surgery off the outer court — herbs, a pallet, the smell of moxa — and planting keeps it busy: cut feet, wrenched backs, whatever the paddies send up. You woke here first.',
          nodeDrillYardBlurbSpring:
            "The old stable court, swept bare, Kihei's ground — but the mud has borrowed half his men, and what drilling survives happens at the dark ends of the day. In his order still, or not at all.",
          nodeHomePaddyBlurbSpring:
            "The guest house's skirts, and in these weeks the whole estate's business: paddies flooded and trodden soft, seedbeds greening, every back the house can spare bent into the water. Everything the house will eat next winter starts here, now.",
          nodeFieldMarginsBlurbSpring:
            "Setts at the paddy's edge — tanuki, badger — and the seedbeds have opened a new front in the quarrel: seed rice standing in open water, and every animal in the margins aware of it. Someone has to walk the beds at dusk, and it is usually you.",
          nodeWoodlotEdgeBlurbSpring:
            'Kindling and forage country past the last fence, and this is the season it feeds people: shoots and greens for the taking, in the weeks before the garden gives anything. No one works out here beside you; no one asks what you carry back.',
          nodeRuinedCompoundBlurbSpring:
            "Beyond the rope and the written warning: roofs fallen in, the great gate down across its own step — the waste ground, by the house's name for it. Every flat of earth on the estate is asked for something this season; this, the widest, is asked for nothing.",
          nodeOvergrownOrchardBlurbSpring:
            'The old orchard gone wild, and blossoming anyway — bramble to the shoulder and flower over it, setting fruit nobody will thin or prune or pick. The dogs den in the hollow, and whelping season has made them worse than winter did.',
          nodeBambooGroveBlurbSpring:
            'Green dark and creaking, close behind the waste ground, and in this season worth digging: the new shoots are food, and the house takes what the monkey troop has not beaten it to. Shinnosuke is forbidden the grove, and is in the grove, with a spade.',
        },
      },
      {
        id: 'c',
        label: 'people-first',
        brief:
          'each node read through its seasonal traffic — who crosses it, what is carried through it',
        scorecard: '11✔ 5✘',
        flavor: {
          nodeWeirRiverbankBlurbSpring:
            "Matsuzō's weir, leased, with the thaw running hard and dirty over the sill; whoever the house can spare is sent down with split bamboo and cord, and Matsuzō meets them at the waterline. The river that gave you up is high enough now that nobody works it alone.",
          nodeWeirReedsBlurbSpring:
            'The reed shallows above and below the weir, past the waist most seasons and past that now; the mending parties wade them roped in pairs, and the flooded-out rats meet them halfway. Matsuzō keeps his count of the screens from the far bank, same as ever.',
          nodeGateGateyardBlurbSpring:
            "The estate's face, and in planting weather a working door: borrowed hands in, borrowed tools out, everything passing the gateposts on its way to the water. Yohei's stall keeps its market days between the posts, and sells more straw sandals than anything else while the planting lasts.",
          nodeForecourtBlurbSpring:
            "The outer court, where the planting is dealt out before it is light — baskets, blades, and who goes to which field, settled here and nowhere else. Genemon's window stands open to it now, and the writing keeps up with the leaving.",
          nodeWoodshedBlurbSpring:
            'The mat, the bowl, the nail — yours still, between woodpiles the winter has eaten low. In planting weather you and the daylight are never in it at the same time.',
          nodeKitchenThresholdBlurbSpring:
            "The threshold where you take your meals, on the outer side of the sill; in planting weather half of O-Hisa's cooking goes past you in carrying-boxes, out to whoever cannot leave the water. The board within still sets the portions, and still talks over your head.",
          nodeShrineCorridorBlurbSpring:
            'The corridor altar, kept as properly as ever; with the seed rice going out, someone has set a first pinch of it among the rites. You pass it more often now, and are asked to stop exactly as often as before.',
          nodeKuraBlurbSpring:
            "The working storehouse, and in seed-time a busy door: what goes out now is next year's harvest in a sack, and Genemon stands to every turn of the lock himself. The grain-watch's stool sits empty by day, for once.",
          nodeSickroomBlurbSpring:
            "Sōan's lean-to surgery off the outer court, where you first woke; planting keeps its doorway busy — feet cut open in the flooded rows, backs gone wrong in the mud. He takes them in the order they can bear to wait.",
          nodeDrillYardBlurbSpring:
            "The old stable court, Kihei's ground. In planting weather he holds his drills at first light and gives the rest of the day up to the paddies — the hours change, his order doesn't.",
          nodeHomePaddyBlurbSpring:
            "The house's skirts and, in planting weather, its whole population: water in, seedlings down, every back the household owns bent over the same mud. What the estate lives on all year is decided in these weeks.",
          nodeFieldMarginsBlurbSpring:
            "The setts at the paddy's edge, and every one of them awake to what planting puts in the ground: seed in the rows draws digging by night, and O-Ume walks her boundary each morning to see what the dark took. The edge rows are planted last, and grudged.",
          nodeWoodlotEdgeBlurbSpring:
            'Kindling and forage country past the last fence, and in thaw weather the first green food anywhere — bracken and butterbur coming up where the snow ran off, gathered before the kitchen has to ask. You are still the only one who works it; a hundred paces in, the sound of the planting stops.',
          nodeRuinedCompoundBlurbSpring:
            "Beyond the rope, the fallen roofs of the waste ground green over with the thaw, weeds standing up in the great gate's doorway. Planting puts the whole household in motion, and none of that motion bends this way — the season's one still place.",
          nodeOvergrownOrchardBlurbSpring:
            'The wild orchard blooms anyway — a week of blossom standing up out of the bramble, visible from the paddies and pointed at from there, which is as close as anyone comes. The dogs keep the yard beneath it.',
          nodeBambooGroveBlurbSpring:
            'The grove in shoot season, and everything that eats knows it: the troop digs at dawn, the kitchen wants its share by noon, and whoever is sent in with a mattock races both. Shinnosuke is forbidden the digging, and is at the digging.',
        },
      },
    ],
  },

  {
    id: 'c5a-nodes-summer',
    title: 'The summer node reads (16 zones)',
    review: 'project/human-in-the-loop/review.md',
    rationale:
      'Take A (work-first) had the strongest Pass-2 verdict; the flagged anchor-span duplications were redlined (de-verbatimed or cut) before landing.',
    canonLabel: 'A · work-first',
    rung: 2,
    takes: [
      {
        id: 'b',
        label: 'senses-first',
        brief: 'each node read through the body standing in it — light, sound, smell, footing',
        scorecard: '10✔ 6✘',
        flavor: {
          nodeWeirRiverbankBlurbSummer:
            "Matsuzō's leased weir at low summer water — the river runs thin and warm over the sill, and the glare comes up off it as hard as the sun comes down. This is where they pulled you out.",
          nodeWeirReedsBlurbSummer:
            'The reed shallows, grown past head height now: inside them the air stops moving, and the water at your shins is as warm as the day. The rats work the leased screens in that cover all summer, and the mending is done half-blind, by feel.',
          nodeGateGateyardBlurbSummer:
            "The swept gateyard, the estate's face to the road: by mid-morning the bare ground gives the heat back, and the road past the posts stands in its own shimmer. On market days Yohei sets his stall tight against the west post and moves it with the shade.",
          nodeForecourtBlurbSummer:
            "The outer court still takes the day's work, but summer sets it out at first light — done early or done in the sun, and by noon the raked ground comes up hot through the soles. Genemon's window stands open onto it now; what is done here is heard as well as seen.",
          nodeWoodshedBlurbSummer:
            'The mat between the woodpiles, and the summer smell that comes with it — cut pine going sharp in the heat, all day and into the dark. The nights are too warm for the coat on its nail; you sleep with the door propped and hear the paddy frogs from here.',
          nodeKitchenThresholdBlurbSummer:
            "Meals are still taken at the threshold, on the outer side of the sill; in summer the eave's shade there is the better seat, and nobody says so. The door stands open for the air, and the board's talk comes over you plainer than ever.",
          nodeShrineCorridorBlurbSummer:
            "The family altar in its corridor, a stripe of cool dark after the yard's glare, the incense hanging in air too still to carry it off. You cross it slower than you need to in this heat; you are still not asked to stop.",
          nodeKuraBlurbSummer:
            'The working storehouse, thick-walled enough to keep its own weather: when the iron-strapped door opens, the air comes out cool and grain-sweet — the one cold thing on the estate by noon. The counting goes on as ever, and the watch stool by the door is suddenly a post nobody minds standing.',
          nodeSickroomBlurbSummer:
            "Sōan's lean-to surgery, hung with the summer's cutting — herbs drying in bunches from the beams, their smell riding thick on the warm air over the moxa's. The screens stand open to the court for whatever air moves; you woke here first.",
          nodeDrillYardBlurbSummer:
            'The old stable court, bare-swept and shadeless by design — which in summer means Kihei drills at grey dawn, and the yard stands empty and beating with heat the rest of the day. His ground, his hours; the heat has only moved them, not loosened them.',
          nodeHomePaddyBlurbSummer:
            "The house's skirts, deep in the green of the year: rice standing to the knee, the paddy water blood-warm around your calves, and the weeds coming faster than the hands that pull them. The rest of the estate is paid for from here — and summer is when the paying is grown.",
          nodeFieldMarginsBlurbSummer:
            "The setts at the paddy's edge, buried now in grass gone waist-high — the ground under it all holes and heat, every step taken on faith. The tanuki is summer-fat and works the racks by night; by day the margins only buzz, and nothing in them owns to anything.",
          nodeWoodlotEdgeBlurbSummer:
            "Forage country past the last fence, and in summer the coolest work there is — full-leaf shade, the smell of leaf-mold and sap, the cicadas so loud the estate couldn't be heard even if it carried this far. When they stop all at once, you stop too.",
          nodeRuinedCompoundBlurbSummer:
            'Beyond the rope, the summer does what the house will not, and covers it: vine over the fallen roofs, bramble to the quarried wall-lines, the great gate down under a season of green. The heat lies still in there — no birds working it that you can hear — and the household calls it the waste ground from a distance.',
          nodeOvergrownOrchardBlurbSummer:
            "The wild orchard at its summer worst: bramble gone over head height, the first windfalls souring under it, wasps on every one. The dogs lie up through the heat of the day and run at dusk — the dogs' yard keeps its name, it just keeps different hours.",
          nodeBambooGroveBlurbSummer:
            "The bamboo's green dark, and in summer the coolest air the estate owns — known to the monkeys, who raid the rows from it, and to Shinnosuke, who is forbidden it. On still afternoons the stems tick and creak with the heat, the nearest thing the grove has to a voice.",
        },
      },
      {
        id: 'c',
        label: 'people-first',
        brief:
          'each node read through its seasonal traffic — who crosses it, what is carried through it',
        scorecard: '9✔ 7✘',
        flavor: {
          nodeWeirRiverbankBlurbSummer:
            "Matsuzō's leased weir, running thin and quiet over the sill — a smaller river than the one they pulled you out of. In the dry weeks every man with a paddy on this water finds a reason to walk the bank, and Matsuzō meets each of them with the same answer: what the river gives.",
          nodeWeirReedsBlurbSummer:
            "The shallows above and below the weir, the reeds gone over head height in the growing heat. Whoever tends the leased screens works blind in the green now, and the rats breed faster than the mending; across the water, Matsuzō's morning starts on his own bank, facing yours.",
          nodeGateGateyardBlurbSummer:
            "The estate's face, posts sound and paint long gone, swept before the heat comes up. On his days Yohei has the stall standing inside the posts by first light and packed away by noon, ahead of the worst of the sun.",
          nodeForecourtBlurbSummer:
            "The guest house's outer court, where the day's work is set out — at dawn now, the one cool hour, the whole household crossing it at once. By midday it stands bare and white with sun, and Genemon's window looks out on nothing moving.",
          nodeWoodshedBlurbSummer:
            'The lean space between the woodpiles — a mat, a bowl, a nail for the coat — smelling of new-split wood already drying for winter. Some mornings there is mending folded on the doorstep, left by hands that did not knock.',
          nodeKitchenThresholdBlurbSummer:
            'Meals are still taken at the sill, on the outer side — though with the cooking fire going all day, summer makes the outside the better seat, and the household knows it. O-Yae comes up from the village early now, while her fish is still worth carrying.',
          nodeShrineCorridorBlurbSummer:
            'The family altar in its corridor, seen in passing and never entered. With the house opened up for air you pass it oftener than winter let you, and most days Toku is there before the heat, changing water that will be warm by noon.',
          nodeKuraBlurbSummer:
            'The working storehouse: rice out, counted, and nothing coming in until autumn. Summer is the counting-down season, and Genemon crosses the court to the iron-strapped door more often than the ledger strictly asks.',
          nodeSickroomBlurbSummer:
            "Sōan's lean-to surgery off the outer court, where you first woke. Summer sends him the fields' custom — sun-struck hands, feet cut open in the paddy mud — and the midday hour brings him more of it than a winter week does.",
          nodeDrillYardBlurbSummer:
            "The old stable court, swept bare; Kihei's ground in any weather. Summer moves the drills to first light and dusk — his one concession to the season — and Shinnosuke is on the wall for both, which nobody has ordered and nobody stops.",
          nodeHomePaddyBlurbSummer:
            "The guest house's skirts, paddy and vegetable rows, the rice standing green and even now. Every hand the house has is in the water by first light, weeding ahead of the sun; by midday the rows are the sun's alone.",
          nodeFieldMarginsBlurbSummer:
            "The wild edge at the paddy's rim, setts and runs hidden in the long grass. Summer feeds the tanuki better than any raid could, but the raids go on regardless — and O-Ume walks the margin path at evening, speaking to no one you can see.",
          nodeWoodlotEdgeBlurbSummer:
            "Kindling and forage country past the last fence, in full leaf now. The village's gathering women work the far slope and you work this one, no path crossing between; a few steps into the green, the estate's sounds close off behind you.",
          nodeRuinedCompoundBlurbSummer:
            'Beyond the rope: fallen roofs, a gate down across its own step, walls quarried low — the waste ground, so called. Summer runs vine and grass over more of it each week, and in the season when every other path on the estate is worn bare with feet, none leads in here.',
          nodeOvergrownOrchardBlurbSummer:
            "The old orchard gone wild, its fruit green and hard on unpruned trees. The dogs lie up in its shade through the day's heat and travel at night, and no errand of the household reaches past the first bramble all summer.",
          nodeBambooGroveBlurbSummer:
            'Green dark behind the waste ground, and the coolest ground the estate keeps — which is why Shinnosuke is in it, forbidden or no. The monkeys raid out of it at their summer boldest, and the vegetable rows pay for every hour the household spends indoors.',
        },
      },
    ],
  },

  {
    id: 'c5a-nodes-winter',
    title: 'The winter node reads (16 zones)',
    review: 'project/human-in-the-loop/review.md',
    rationale:
      'Take A (work-first) had the strongest Pass-2 verdict; the flagged anchor-span duplications were redlined (de-verbatimed or cut) before landing.',
    canonLabel: 'A · work-first',
    rung: 2,
    takes: [
      {
        id: 'b',
        label: 'senses-first',
        brief: 'each node read through the body standing in it — light, sound, smell, footing',
        scorecard: '12✔ 4✘',
        flavor: {
          nodeWeirRiverbankBlurbWinter:
            'Ice stands in plates along the leased screens, and the river runs thin and black over the sill, quieter than you have yet heard it. The water they pulled you out of would not give a man back in this season.',
          nodeWeirReedsBlurbWinter:
            "The reeds stand dead past the waist and rattle at a touch; the shallows at the bank have set hard enough to take a boot's weight. Under the ice the rats are still at Matsuzō's screens — you hear the gnawing before you see anything at all.",
          nodeGateGateyardBlurbWinter:
            "Snow is swept from between the gateposts before it can settle — the estate's face, kept bare in the cold as it is kept in every season. On market days Yohei's stall still sets up inside the posts, thinner-stocked, his breath white over the goods.",
          nodeForecourtBlurbWinter:
            "The outer court rings underfoot, frozen past raking; the day's work is set out by men blowing on their hands. Genemon's lamp burns behind his window before first light now — what is done here is still written down.",
          nodeWoodshedBlurbWinter:
            'The woodpiles you sleep between go down the way all stores go down now, and the space that is yours widens by the week. The mat, the bowl, the nail for the coat — though the coat sleeps on you in this season, not on the nail.',
          nodeKitchenThresholdBlurbWinter:
            "Meals still come to the outer side of the sill, and winter takes the sill's measure — pot-steam and lamplight within, the yard's cold standing at your back. The household's talk rides out on the warm air; none of it is aimed at you.",
          nodeShrineCorridorBlurbWinter:
            "The corridor keeps the house's cold, and the altar keeps its one flame against it; the incense reads sharper on air cold enough to show your breath. You pass through, and you are still not asked to stop.",
          nodeKuraBlurbWinter:
            "The kura answers with an echo autumn never heard: rice out, counted, and less behind the count every week. The lock's iron takes the skin of a bare hand at morning; the grain-watch keeps the stool where the wall cuts the wind.",
          nodeSickroomBlurbWinter:
            "Sōan's lean-to surgery does its season's business — the valley's coughs come to it now, and the moxa smell carries clear across the court on the cold air. The pallet you woke on stands nearest the brazier, and is rarely empty.",
          nodeDrillYardBlurbWinter:
            "The old stable court is swept of snow before drill and frozen flat again by the end of it; Kihei's order does not shorten for the cold. The ground takes a fall harder in this season, and your breath stands white between strokes.",
          nodeHomePaddyBlurbWinter:
            'The paddies stand drained to frozen stubble, and the plainest work turns to mending — bunds, channels, handles — with hands that lose the feel of the tool by mid-morning. What the house eats until spring is in the vegetable rows, under straw.',
          nodeFieldMarginsBlurbWinter:
            "The snow at the paddy's edge takes the night's record plainly: badger and tanuki printed from their setts to the seed store and back, fresher each morning than the last. The drying racks stand empty, so the seed store carries the whole of their attention.",
          nodeWoodlotEdgeBlurbWinter:
            "Past the last fence the snow lies unbroken except where you broke it last; the kindling country is yours alone, as ever. The estate's sounds stop sooner in snow — fifty paces now — and after that it is your boots, your breath, and a branch somewhere letting go its load.",
          nodeRuinedCompoundBlurbWinter:
            'Snow lies unbroken over the waste ground past its rope, drawing the fallen roofs as long white ridges and the downed gate as a stair of drift. Nothing has crossed it — not the dogs, not once all season.',
          nodeOvergrownOrchardBlurbWinter:
            "The old orchard's bramble stands bare as wire and the windfall lies frozen too hard to rot; the dogs are down in the hollow with less between them and hunger than any season yet. You smell the den on the cold air before you hear anything move.",
          nodeBambooGroveBlurbWinter:
            'The grove is the one green left standing in the valley, and the snow load makes it loud — culms bowing, then the crack and slide of a stem letting go. The monkeys sit high and raid little; Shinnosuke is forbidden the grove, and his prints go in anyway, small and unhurried.',
        },
      },
      {
        id: 'c',
        label: 'people-first',
        brief:
          'each node read through its seasonal traffic — who crosses it, what is carried through it',
        scorecard: '6✔ 10✘',
        flavor: {
          nodeWeirRiverbankBlurbWinter:
            "Matsuzō's weir, leased, its screens the house's to keep whole; in the deep cold he walks his bank once a day, muffled to the eyes, and no one else comes down to the water at all. The river that gave you up runs black and slow between rims of shore ice.",
          nodeWeirReedsBlurbWinter:
            'The shallows above and below the weir, the reeds standing dead and pale past the waist. The rats gnaw the leased screens worst in the lean months, and the mending falls to whoever can stand the wading — you, mostly, working the lashings for as long as the hands last, and no longer.',
          nodeGateGateyardBlurbWinter:
            "The estate's face, kept barely and kept anyway — the snow swept from between the posts each morning, mostly for the sweeping's sake. Yohei's stall still sets up inside them when the road lets him through, and on those mornings the gateyard holds more of the village than the season otherwise allows.",
          nodeForecourtBlurbWinter:
            "The outer court of the guest house, where the day's work is still set out and seen to — later now, and less of it, the short days closing in from both ends. Genemon's lamp burns behind his window before the yard has emptied, and what is done here is written down by that light.",
          nodeWoodshedBlurbWinter:
            'A lean space between the woodpiles — mat, bowl, nail — and all winter the household comes to your walls for its fuel. The piles shrink around your sleep armload by armload; by thaw the room will be twice the size and none the warmer.',
          nodeKitchenThresholdBlurbWinter:
            "Meals are taken at the threshold, on the outer side of the sill, and winter makes the arrangement honest: the fire's heat stops at the sill exactly, and the board's talk comes muffled through a door kept shut. The bowl is handed out steaming and finished cold.",
          nodeShrineCorridorBlurbWinter:
            'A family altar set into a corridor never meant to hold it, and the rites kept through the cold all the same — someone is there before the house wakes, breath hanging over the lamp-flame in the dark mornings. You pass it; you are not asked in.',
          nodeKuraBlurbWinter:
            "The working storehouse, iron-strapped and locked, and in winter the house's whole arithmetic: rice goes out now and nothing comes in, every measure counted against the cold months still owed. The grain-watch's stool has a fire-box beside it these nights, and the watch is kept in earnest.",
          nodeSickroomBlurbWinter:
            "The lean-to surgery off the outer court, and winter is its busy season: coughs, chilblains, an old man's chest, carried up from the village on somebody's back. Sōan burns more moxa than lamp-oil these months, and the pallet you woke on is seldom empty long.",
          nodeDrillYardBlurbWinter:
            "The old stable court, swept of snow before anything else on the estate is, because Kihei's order does not keep a season. Drill is at dawn on ground rung hard as board, and the stave stings the hands that hold it wrong — the cold, he says, finds a loose grip faster than he can.",
          nodeHomePaddyBlurbWinter:
            "The guest house's skirts, the paddies drained to frozen stubble, the plainest work turned to mending: bunds to shore, straw to twist, the winter greens picked over by whoever the kitchen sends out. The field that pays for everything gives nothing now until spring.",
          nodeFieldMarginsBlurbWinter:
            "Setts at the paddy's edge, quiet under the snow — the tanuki sleeps fat on what it stole from the racks, and the badger keeps to its dig. The snow does the watching now: every night's track to the seed store is laid down plain, and whoever walks out first in the morning reads it.",
          nodeWoodlotEdgeBlurbWinter:
            "Kindling and forage country past the last fence, and the one path on the estate walked more in winter than out of it — the house burns wood faster than the axe stacks it, and the track out is yours alone, trodden black through the snow. Fifty paces in, the snow-hush swallows the estate's sounds entire.",
          nodeRuinedCompoundBlurbWinter:
            'Beyond a rope and a written warning: fallen roofs, a downed gate, walls quarried low, all of it under snow now. It is the one ground on the estate the snow keeps unbroken all winter — no errand crosses it, no shortcut, no track but what the dogs leave.',
          nodeOvergrownOrchardBlurbWinter:
            'The old orchard gone wild, the bramble bare and black over the snow — and the dogs denned in the hollow are boldest now, in the season with the least to eat. They come as far as the fence line at dusk to watch the house, and nobody is sent past this way alone after dark.',
          nodeBambooGroveBlurbWinter:
            'Green dark under the snow — the one green left standing on the estate — and loud with it, the stems shedding their loads all day in sudden crashes. The troop keeps deep in the grove through the cold and the vegetable rows get their peace; Shinnosuke is forbidden the grove in winter above all, and is in the grove.',
        },
      },
    ],
  },

  {
    id: 'c5a-overlays',
    title: 'The five season-turn overlays',
    review: 'project/human-in-the-loop/review.md',
    rationale:
      "Take C scored 18✔2✘ — the land-first frame keeps people late and small (the register the nengu frame set), every image this valley's own; redlined per Pass-2 (one stacked ornament; the handoff template broken in 3 of 5).",
    canonLabel: 'C · the land first',
    rung: 2,
    takes: [
      {
        id: 'a',
        label: 'the ledger turns',
        brief: 'every season closes as work and accounting; emotion only through what gets counted',
        scorecard: '12✔ 7✘',
        scenes: {
          'turn-winter': {
            id: 'turn-winter',
            voice: 'narrator',
            motivates: [],
            greeting: [
              {
                voice: 'narrator',
                text: 'The wood is counted down to what is left, and what is left is not measured against anything — winter has stopped asking. The mended things go back to their walls, each worth a line the day it broke and none now that it holds.',
              },
              {
                voice: 'narrator',
                text: 'Before the year is let go, the book settles with it. What the house owes is called in and answered where it can be; what cannot be answered is not forgiven — it is carried forward, in a hand that writes smaller than the rest of the page.',
              },
              {
                voice: 'narrator',
                text: 'Genemon warms water for the ink stone — the one warmth the board room is allowed — and rules the year off. A whole winter fits above the line.',
              },
              {
                voice: 'narrator',
                text: 'The New Year is at the gate before the ink is dry. A fresh page, headed and empty: the one morning of the year the book holds nothing against anyone. It does not last the week.',
              },
            ],
            topics: [],
            decision: { prompt: '', options: [] },
          },
          'turn-new-year': {
            id: 'turn-new-year',
            voice: 'narrator',
            motivates: [],
            greeting: [
              {
                voice: 'narrator',
                text: 'The pine comes down from the gate and goes to the kitchen as kindling. The house does not keep ornaments; it keeps what they burn down to, and the book sees it to the stove.',
              },
              {
                voice: 'narrator',
                text: 'The quiet weeks end at the board first. The entries turn forward: so much seed against so many rows, so many days between here and the water. For a season the book will count things that do not exist yet. It is the only reckless thing the book does all year.',
              },
              {
                voice: 'narrator',
                text: 'The dull tools go down to the smith and come back with edges. The charge is entered against a season that has not earned anything.',
              },
              {
                voice: 'narrator',
                text: 'The New Year is spent down to its last slow morning. Spring next — the page is already headed, and the paddies are already in it.',
              },
            ],
            topics: [],
            decision: { prompt: '', options: [] },
          },
          'turn-spring': {
            id: 'turn-spring',
            voice: 'narrator',
            motivates: [],
            greeting: [
              {
                voice: 'narrator',
                text: 'The last row goes in with the light failing, and the count goes into the ground with it. What left the kura as a number stands in the mud as rows, and cannot be counted again until autumn proves it.',
              },
              {
                voice: 'narrator',
                text: "The book holds the difference as owing. The ground has the house's rice now, and the ground signs nothing.",
              },
              {
                voice: 'narrator',
                text: 'The planting days are entered by the day, never by the back that did them. Yours are in there with the rest, worth the same line.',
              },
              {
                voice: 'narrator',
                text: 'Spring is finished the hour the water stands level in the last paddy. Summer takes the valley from the morning side — heat before the dew is off — and the book goes quiet, waiting on the ground.',
              },
            ],
            topics: [],
            decision: { prompt: '', options: [] },
          },
          'turn-summer': {
            id: 'turn-summer',
            voice: 'narrator',
            motivates: [],
            greeting: [
              {
                voice: 'narrator',
                text: 'The weeding is done for the doing again, and the walking begins. Genemon goes along the paddy dikes with the day-book shut under his arm. Whatever he counts out there he counts by eye, and enters none of it.',
              },
              {
                voice: 'narrator',
                text: 'The Bon goods come up from the stall: lamp-oil, incense, cut flowers. They are entered like any goods, at their price. It is the one page of the year nobody argues smaller.',
              },
              {
                voice: 'narrator',
                text: 'The kitchen cooks ahead. The tools are set down where they stand. Work is entered until it stops, and then the stopping is not entered at all.',
              },
              {
                voice: 'narrator',
                text: 'Summer closes with the valley still green and unproven. Bon is next, and the house turns from what it grows to what it owes its dead.',
              },
            ],
            topics: [],
            decision: { prompt: '', options: [] },
          },
          'turn-bon': {
            id: 'turn-bon',
            voice: 'narrator',
            motivates: [],
            greeting: [
              {
                voice: 'narrator',
                text: 'Three days stand blank in the day-book — the only blank the year is allowed. Genemon rules around them and leaves them, the way a field is left around a stone.',
              },
              {
                voice: 'narrator',
                text: 'What the lamps burned is not asked after. The oil went where it was sent; no line calls it back. Of everything the house buys in a year, only this is spent without being watched.',
              },
              {
                voice: 'narrator',
                text: 'Then the blank days are spent too. Autumn, and the book sharpens with the sickles: whetstones out, racks up, everything that grew on trust about to be weighed. The book has been patient since spring. It is done being patient.',
              },
            ],
            topics: [],
            decision: { prompt: '', options: [] },
          },
        },
      },
      {
        id: 'b',
        label: "the household's body",
        brief: "the turn read in the house's physical routine; the family glimpsed at its seams",
        scorecard: '3✔ 3✘',
        scenes: {
          'turn-winter': {
            id: 'turn-winter',
            voice: 'narrator',
            motivates: [],
            greeting: [
              {
                voice: 'narrator',
                text: "Winter leaves the house through the back door, in baskets. Brazier ash, the year's soot knocked off the beams with green bamboo — the kitchen works with everything open, letting in cold it spent three months keeping out.",
              },
              {
                voice: 'narrator',
                text: "The good bowls come down off the high shelf, unwrapped onto trays you have not seen used. Behind the screens the family is up late; the light in the main room outlasts the house's habit.",
              },
              {
                voice: 'narrator',
                text: 'Rice is set to soak in the big tub — not an eating quantity. A pounding quantity.',
              },
              {
                voice: 'narrator',
                text: 'Winter is done. The house has swept it out and shut the door on it, and what it opens the front gate to instead, it calls the New Year.',
              },
            ],
            topics: [],
            decision: { prompt: '', options: [] },
          },
          'turn-new-year': {
            id: 'turn-new-year',
            voice: 'narrator',
            motivates: [],
            greeting: [
              {
                voice: 'narrator',
                text: 'The New Year is put away in the order it came out. Bowls wrapped and boxed to the storehouse shelf, the gate greens taken down and burned small, the last of the pounded rice gone hard enough for the soup pot and nothing else.',
              },
              {
                voice: 'narrator',
                text: 'The pegs by the kitchen door change: town cloth in, work cloth out. Hoes and mattocks stand along the yard wall to have their handles looked at, one by one.',
              },
              {
                voice: 'narrator',
                text: 'Mud takes the threshold stones again. Nobody wipes it twice.',
              },
              {
                voice: 'narrator',
                text: 'The visiting season is over. What stands in the yard now, wanting hands before it wants anything, is spring.',
              },
            ],
            topics: [],
            decision: { prompt: '', options: [] },
          },
          'turn-spring': {
            id: 'turn-spring',
            voice: 'narrator',
            motivates: [],
            greeting: [
              {
                voice: 'narrator',
                text: 'The house changes its skin. The papered doors come off their grooves and go up to the loft, and reed screens hang in their places; for one afternoon, between the old walls and the new, you can see straight through the house — the family at their low tables, arranged just as they were behind the paper.',
              },
              {
                voice: 'narrator',
                text: "Quilts go over the poles in the sun. Padded jackets come apart under Chiyo's needle into flat cloth, washed, and folded away with the winter.",
              },
              {
                voice: 'narrator',
                text: 'The lamps are filled less. The day does most of the lighting now, and stays in the rooms longer than anyone asks it to.',
              },
              {
                voice: 'narrator',
                text: 'Spring is finished. What the opened house lets through it, wall to wall, is summer.',
              },
            ],
            topics: [],
            decision: { prompt: '', options: [] },
          },
          'turn-summer': {
            id: 'turn-summer',
            voice: 'narrator',
            motivates: [],
            greeting: [
              {
                voice: 'narrator',
                text: 'Field tools go up early and stay up. The house turns inward instead: the altar room is opened and aired, and Chiyo wipes each tablet and sets it back before the next comes down. You are not asked to help with that room.',
              },
              {
                voice: 'narrator',
                text: 'Lanterns come out of the loft and are checked for their candles — more lanterns than the house has people.',
              },
              {
                voice: 'narrator',
                text: 'At dusk a small fire is laid at the gate, ready for lighting. The doors are left open onto the dark, for guests who will not come by the road.',
              },
              {
                voice: 'narrator',
                text: 'Summer is over. The season the house has dressed itself for is Bon.',
              },
            ],
            topics: [],
            decision: { prompt: '', options: [] },
          },
          'turn-bon': {
            id: 'turn-bon',
            voice: 'narrator',
            motivates: [],
            greeting: [
              {
                voice: 'narrator',
                text: "A second fire at the gate, at dusk, to send off what the first one welcomed. The lanterns are emptied of their stubs and go back to the loft; the altar room is closed, and the house's doors remember their daytime shapes.",
              },
              {
                voice: 'narrator',
                text: 'The loft gives up sacks and carrying-baskets next, down into the yard for mending. The kura is swept out to the bare boards — emptied, so far as you can see, in order to be filled.',
              },
              {
                voice: 'narrator',
                text: 'The kitchen fires earlier and cooks longer. Whatever is coming will be fed in the field, out of boxes, at the hours the work sets.',
              },
              {
                voice: 'narrator',
                text: 'Bon is over, and its guests are seen off. The house stands with its hands full, facing autumn.',
              },
            ],
            topics: [],
            decision: { prompt: '', options: [] },
          },
        },
      },
    ],
  },

  {
    id: 'fb324-rake-cap',
    title: 'The rake-cap line (the spill runs out)',
    review: 'project/feedback-human/2026-07-10-playtest-work-actions.md',
    rationale:
      'Take A landed in the 2026-07-10 drain proposal (wholesale go-ahead) — plain narrator fact, the FB-91 rake-narration register, no maxim; wired switchable per ADR-139 so the pick can be overridden in play.',
    canonLabel: 'A · nothing left on the boards',
    rung: 0,
    takes: [
      {
        id: 'b',
        label: 'you notice the repetition',
        brief:
          "the exhaustion read through the MC's own hands — the work answers before the narrator does",
        scorecard: '(offered in-thread, unscored)',
        flavor: {
          rakeCapLine:
            'You rake where you have already raked. The boards are clean; the basket is as full as it will get.',
        },
      },
      {
        id: 'c',
        label: 'Genemon closes it',
        brief: "the steward's voice ends the job — the house notices the yard is clean",
        scorecard: '(offered in-thread, unscored)',
        flavor: {
          rakeCapLine:
            'Genemon looks the floor over once. "The yard is clean. Find a wall and rest against it."',
        },
      },
    ],
  },

  {
    id: 'fb362-intro-titles',
    title: "The cold open's act titles (the 幕-heads)",
    review: 'project/human-in-the-loop/review.md',
    rationale:
      'Take B names each act by its event — what the water takes / no name to give / entered in the book — every head canon-true and on-screen in its own act, distinct at a glance (TST4), and the genemon head points straight at the open day-book entry the R7 name-writing pays off (TST3). Take A invents a "book room" the game has no place for; take C\'s clock labels blur at a glance and misdate the soan act.',
    canonLabel: 'B · event-named acts (taken / unnamed / entered)',
    rung: 0,
    takes: [
      {
        id: 'a',
        label: 'the ground it happens on',
        brief:
          "place-anchored — each head names WHERE the act happens; the world's geography carries the story (river → sickroom → book room).",
        scorecard: '19✔ 1✘ 1— (✘ P10 [blind spot] — "the book room" is not a place the game has)',
        introTitles: {
          dream: 'the river',
          soan: 'the sickroom',
          genemon: 'the book room',
        },
      },
      {
        id: 'c',
        label: 'the counted week',
        brief:
          'time-anchored — the first days as a counted sequence; time as the thing the house counts and writes (before the first day → three days fevered → the fourth day).',
        scorecard:
          '19✔ 1~ 1— (~ TST4 [blind spot] — two clock-shaped heads blur; the soan head misdates its act)',
        introTitles: {
          dream: 'before the first day',
          soan: 'three days fevered',
          genemon: 'the fourth day',
        },
      },
    ],
  },

  {
    id: 'hd30-nengu',
    title: 'The nengu reckoning — the year against the house',
    review: 'project/human-in-the-loop/review.md',
    rationale:
      "Take C keeps the strictest §0.5 register — the sum is never read aloud, the MC is counted as part of the room and not asked into it, and the house's true size is learned only by what it lets itself be seen without. No epigram, no priced aside; the felt-never-numbered debt carries wordless.",
    canonLabel: 'Take C — the speakerless reckoning (MC as furniture)',
    rung: 2,
    takes: [
      {
        id: 'a',
        label: "Chiyo's arithmetic of dignity",
        brief:
          "Chiyo centered — the house will not PERFORM falling; the nengu paid by inches it SHOWS (a screen sold quietly from a far room, the gate kept painted). The land-tax felt, never numbered; the MC holds the lamp and learns the house's size by what it will be seen without.",
        scorecard:
          "strong TST3/TST4; risk — Chiyo's priced asides edge toward explaining the arithmetic the canon leaves unspoken.",
        scenes: {
          'nengu-autumn-frame': {
            id: 'nengu-autumn-frame',
            voice: 'narrator',
            motivates: [],
            greeting: [
              {
                voice: 'narrator',
                text: 'Autumn thins to its last week, and the board is lit at an hour that is not for hands. The household stands along the walls. You are sent for, to carry a lamp and hold it steady — light to read a reckoning by, and no more part in it than that.',
              },
              {
                voice: 'narrator',
                text: 'The day-book lies open. Genemon reads what the land owes against what the store holds, and the two do not meet. He does not say by how much. In this house the figure is never spoken; it is only stood in front of, the way the room is standing in front of it now.',
              },
              {
                voice: 'narrator',
                text: 'Above the book, where the lamp does not quite reach, Chiyo does the other arithmetic — the one that is never entered.',
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.chiyo,
                text: '"The painted screen from the far room. It goes to the town, quietly, and not through the valley. The room keeps its door shut after. No one is shown a bare wall."',
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.chiyo,
                text: '"If the screen sells well, the gate is painted before the New Year. If it sells poorly, the gate is painted still, and the far room gives up its second screen for it."',
              },
              {
                voice: 'narrator',
                text: "No one asks the hired man what a screen is worth, or by what road such things leave a house, or how a year's due is paid out of the walls of one's own quiet rooms. You hold the lamp. You are learning the house's true size by what it will let itself be seen without.",
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.chiyo,
                text: '"Enter it met. What we cannot pay in rice we will pay in screens, and no one at the gate will know the difference."',
              },
              {
                voice: 'narrator',
                text: 'Genemon dips his brush and enters one line — the reckoning met, though you never hear with what. The book closes. The lamps are taken up. Autumn is let go with no word raised in the whole house: the silence the house has just paid for.',
              },
            ],
            topics: [],
            decision: { prompt: '', options: [] },
          },
        },
      },
      {
        id: 'b',
        label: 'The day-book register',
        brief:
          'The year closed as an ENTRY — Genemon foots the last column and reads it down, item/count/condition, the whole house standing to hear its own account. The one line he never reads as a figure is the nengu; the MC is carried in the book as a mark, not yet a name.',
        scorecard:
          'strong "felt never numbered" via Genemon-who-counts-but-won\'t; risk — the ledger register runs colder, the MC\'s presence thinner than the canon corner.',
        scenes: {
          'nengu-autumn-frame': {
            id: 'nengu-autumn-frame',
            voice: 'narrator',
            motivates: [],
            greeting: [
              {
                voice: 'narrator',
                text: "The board, the year's turn. Genemon foots the last column of the season by lamplight and reads it down aloud — item, count, condition — the whole house standing to hear its own account. You stand at the edge of it. Nobody set you there. Nobody sends you off.",
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"Autumn rice: in, weighed, under the store roof. Woodlot: cut and stacked for winter, dry. Weir screens: three mended, one owed downstream against the spring. The reclaimed rows bore — entered to the house, not to any wage."',
              },
              {
                voice: 'narrator',
                text: 'Behind the board a paler square marks the wall where a screen used to hang. Nobody looks at it. Nobody names what met the difference. The house does not discuss its arithmetic; it settles it in rooms you are not shown.',
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"And the nengu. The year\'s tax against the year\'s yield. Paid. The store stands level after it — level, not over. That closes the season."',
              },
              {
                voice: 'narrator',
                text: 'He names the tax and does not count it — the one line at this board he never reads as a figure. The house carries it wordless, and every man standing feels the size of it anyway.',
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"The book is the better for a hand on it this year. The store, the screens, the rows — held or gained, each entered. The hand that did it carries no name yet. The account holds a mark as well as it holds a name."',
              },
              {
                voice: 'narrator',
                text: "He dips the brush, enters the season closed, and squares the day-book with the lamp. The year is reckoned. You are in its book — the season's wages set against a mark where a name has not been written — and you are still at its edge.",
              },
            ],
            topics: [],
            decision: { prompt: '', options: [] },
          },
        },
      },
    ],
  },

  {
    id: 'hd36-lord-unstaged',
    title: "The lord unstaged — R7's word through the wall",
    review: 'project/human-in-the-loop/review.md',
    rationale:
      "Take B ties the scorecard and wins on TST3 — R7's title (\"Trusted of the house\") needs the house's summit to have noticed, and B delivers that notice inside the bible's own device: heard through the shoji, unanswered, never met — pre-sounding the damaged voice T1's shoin capstone pays off.",
    canonLabel: 'Take B — the voice through the wall',
    rung: 7,
    takes: [
      {
        id: 'a',
        label: 'The word comes down the wall',
        brief:
          "The lord's words reach the yard only RELAYED — Genemon flattens the maxim into the day-book, Chiyo re-prices his answer into what need not be sold; his attention crosses the wall, the wall itself stays whole.",
        scorecard:
          '5✔ · 1✘ · 15— (✘ TST4 [briefed] — the granary line lands two inferences from the banked rice and centers the carrier over the milestone)',
        reqFlavor: {
          'the-fields-run-on-you': `"${NAMES.lord} says the fields have learned their own weather," ${NAMES.elder} relays at the board, and writes in the day-book: the rows were in before the order came down.`,
          'a-granary-against-winter': `${NAMES.steward} carried the granary word in through the shoji herself; what came back out was hers: "This winter, no screen leaves the quiet rooms."`,
        },
      },
      {
        id: 'c',
        label: 'The house answers for him',
        brief:
          "No lord at all — the estate testifies institutionally (the paddies keeping his hours, a day-book page unused for years); absence as presence, recognition as fact rather than anyone's speech.",
        scorecard:
          "6✔ · 0✘ · 15— (register-safest; withholds the lord's notice that R7's title promises — the TST3 tie-break loser)",
        reqFlavor: {
          'the-fields-run-on-you':
            'The paddies keep his hours now; the hands go out when he goes out, and no one remembers the last order given.',
          'a-granary-against-winter': `There is rice against winter in the kura, and ${NAMES.elder} opens a page in the day-book the house has not needed in years to say so.`,
        },
      },
    ],
  },

  {
    id: 'works-cause',
    title: 'The works discovery chain (estate Phase 1)',
    review: 'project/human-in-the-loop/review.md',
    rationale:
      'Take C\'s sighting lines are the FB-338 fix itself — the damage caught mid-act answers "why did you notice it\'s broken" on sight; naming lines are location-free (mechanically safest) and the land-first register matches the c5a-overlays canon pick. Redlined per Pass-2 (sill→weir; the kura fullness claim softened).',
    canonLabel: 'C · the land keeps its own book',
    rung: 2,
    takes: [
      {
        id: 'a',
        label: "the ledger's mercy",
        brief:
          "every reveal is a day-book entry read aloud; Genemon's care voiced only as bookkeeping choices; being entered is being kept",
        scorecard:
          '17✔ 4✘ (2 [blind spot]: later beats assert prior completions — mooted by the ladder-order gate; naming lines pin "at the board"; U1 label reads abstract on the ladder)',
        scenes: {
          'works-intro': {
            id: 'works-intro',
            voice: 'steward',
            speaker: 'genemon',
            motivates: [],
            greeting: [
              {
                voice: 'narrator',
                text: "Morning, the forecourt. Genemon's window stands open on the board and the day-book is out — turned back, for once, from the working page to the old ones, the corners gone soft with handling. He does not call you. He waits until the round brings you past, which he knew it would.",
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"Stand where you can see the page. This concerns you now — you are on the book, and the book has other lines than wages."',
              },
              {
                voice: 'narrator',
                text: 'He reads them the way he reads everything, at board volume, one figure at a time, and each one lands like a thing set down and not thrown.',
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"Gate. Posts, two, sound. Paint, gone. Lower hinge, crying since the year before your river. Carried open four years. Charged to no man."',
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"Home paddies. Bunds, slumping at the water-edges — two hand-spans lost to the mud each spring, entered each spring, mended never. Carried open six years."',
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"Woodshed. Roof, north slope, taking water. The damp goes against the house. The man who sleeps under the damp does not."',
              },
              {
                voice: 'narrator',
                text: 'He turns further back, to a page ruled tighter than the rest, and lays the book flat so the line faces you.',
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"And this one, which does not close. Weir screens, leased of Matsuzō, across the river. The rats gnaw them at the waterline; every slat gnawed is coin owed off the estate. I have entered that damage every season for nine years, the way I enter rain. The weir path is open to you. It was always the house\'s to walk; it is time a hand walked it."',
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"That is the reading. Now the checking. Go and stand where the figures stand — the gate, the paddies, your own roof. An entry is worth what the eye that checks it is worth. Come back when you have looked."',
              },
            ],
            topics: [
              {
                id: 'works-intro-ask-a',
                label: '"Why show me the book?"',
                answer: [
                  {
                    voice: 'steward',
                    speaker: NPC_NAME.genemon,
                    text: '"Because you are in it. A man on this book eats from these figures whether he reads them or not — every open line above your line leans on your line. The others know the yard by their years here. You will know it the way I know it, or not at all."',
                  },
                ],
              },
              {
                id: 'works-intro-ask-b',
                label: '"Who pays for the mending?"',
                answer: [
                  {
                    voice: 'steward',
                    speaker: NPC_NAME.genemon,
                    text: '"Not the house — its coin is spoken for before it reaches the yard; that was your first day\'s lesson and it has not improved. What a hand puts in of his own — labour, and coin when he has coin — the book carries under his mark, dated. It forgets nothing it is given. That is the one promise I am in a position to make."',
                  },
                ],
              },
            ],
            decision: {
              prompt: 'The day-book lies open on the named lines.',
              options: [
                {
                  id: 'works-intro-go',
                  label: '"I\'ll walk them today."',
                  say: '"I\'ll walk them today."',
                  react:
                    '"Then the book says so. A short line, the date, no more. Named, and walked the same day — the date will keep you honest."',
                  memory: [{ npc: 'genemon', warmthDelta: 1, regard: 'walked-the-book' }],
                  flags: ['works-named-u1', 'works-named-weir'],
                },
                {
                  id: 'works-intro-hold',
                  label: '"Figures aren\'t mending. The yard wants me first."',
                  say: '"Figures aren\'t mending. The yard wants me first."',
                  react:
                    '"The yard is on this book too; I am not asking you off it. The lines stay open. Open is what they are used to. The old pages keep their corners."',
                  flags: ['works-named-u1', 'works-named-weir'],
                },
              ],
            },
          },
          'works-u1': {
            id: 'works-u1',
            voice: 'steward',
            speaker: 'genemon',
            motivates: [],
            greeting: [
              {
                voice: 'narrator',
                text: "The board, evening. The day-book stands open at a page ruled fresh — the gate's line, the bunds' line, the woodshed's, copied out of their years and set together under one head, in ink already dry. He did the copying before you had crossed the yard.",
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"The gate hinge. The bunds. The slope over your mat. You stood where the figures stand. Tell me if they lied."',
              },
              { voice: 'player', text: '"They didn\'t."' },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"They never do. That is the whole of their use."',
              },
              { voice: 'narrator', text: 'He rules a bracket down the three lines, joining them.' },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"One work, then, taken as one: board and hinge-iron for the gate, clay and stone for the water-edges, straw for the roof. The tools are the house\'s — their wear I enter against the house, as I enter my own pen. The rest goes in as you can put it in, and is entered as it goes."',
              },
            ],
            topics: [
              {
                id: 'works-u1-ask-a',
                label: '"Why these three first?"',
                answer: [
                  {
                    voice: 'steward',
                    speaker: NPC_NAME.genemon,
                    text: '"Because they are the oldest lines still cheap. The gate has waited four years; a fifth costs a post. The bund gives up more each spring than the spring before. Old damage compounds like a debt, and these three have not yet learned to. Close them before they do."',
                  },
                ],
              },
              {
                id: 'works-u1-ask-b',
                label: '"And when it\'s done?"',
                answer: [
                  {
                    voice: 'steward',
                    speaker: NPC_NAME.genemon,
                    text: '"The line closes. A house stands on closed lines; there is no other material. And the closing carries the mark of the hand that closed it — dated, footed, kept. The yard forgets a mended hinge by the next rain. The book does not."',
                  },
                ],
              },
            ],
            decision: {
              prompt: 'Three lines under one head, and the brush is wet.',
              options: [
                {
                  id: 'works-u1-begin',
                  label: '"Put my mark to it."',
                  say: '"Put my mark to it."',
                  react:
                    '"Entered. First repairs — opened this day, against the hand that walked them. Three of the book\'s oldest lines now point at you."',
                  memory: [{ npc: 'genemon', warmthDelta: 1, regard: 'set-to-it' }],
                  flags: ['works-open-u1'],
                },
                {
                  id: 'works-u1-hold',
                  label: '"When I have the means. Not before."',
                  say: '"When I have the means. Not before."',
                  react:
                    '"That is the correct order, and rarer than it should be. The head is ruled either way — it waits better than the timber will. Bring your mark when you bring the means."',
                  flags: ['works-open-u1'],
                },
              ],
            },
          },
          'works-u2': {
            id: 'works-u2',
            voice: 'steward',
            speaker: 'genemon',
            motivates: [],
            greeting: [
              {
                voice: 'narrator',
                text: 'The board. Genemon has the day-book turned back further than you have seen it go — years back, to a line ruled open and never carried forward, never struck, sitting on its page like a door left unlocked in an empty house.',
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"Orchard. Last entered bearing the year before the dogs. Since then — nothing against it. No fruit, no loss, no line. I did not strike it. A struck line is a judgment. This one was only waiting."',
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"You have seen the rows now. Sound trees under the choke, planted in order by people who expected to walk between them. Wild wood over the top, and the dens under. Clearing it is billhook work and den work — weeks of the one, and the other is not work I can enter for you."',
              },
            ],
            topics: [
              {
                id: 'works-u2-ask-a',
                label: '"You kept the line open all this time?"',
                answer: [
                  {
                    voice: 'steward',
                    speaker: NPC_NAME.genemon,
                    text: '"Ink is cheap. Judgment is not. Whoever set those trees out expected paths, and lanterns, and years — I do not close another man\'s expectation until it is proved wrong. Six stewards\' worth of pages carry that line open. I was not going to be the one who ended it for tidiness."',
                  },
                ],
              },
              {
                id: 'works-u2-ask-b',
                label: '"And the dogs?"',
                answer: [
                  {
                    voice: 'steward',
                    speaker: NPC_NAME.genemon,
                    text: '"Kihei\'s column, not mine. Mine says only this: the year the dens filled, the fruit line emptied. The book has watched the two trade for six years, one for one. Empty the dens, and I know what the other line does. I have the figures."',
                  },
                ],
              },
            ],
            decision: {
              prompt: 'The open line waits on the page.',
              options: [
                {
                  id: 'works-u2-begin',
                  label: '"Then it stops waiting."',
                  say: '"Then it stops waiting."',
                  react:
                    '"Ground taken back. I have not ruled those words in thirty years of this book. They are ruled now, and dated. Prove the ink right."',
                  memory: [{ npc: 'genemon', warmthDelta: 1, regard: 'ground-taken-back' }],
                  flags: ['works-open-u2'],
                },
                {
                  id: 'works-u2-hold',
                  label: '"It has waited years. It can wait for my strength."',
                  say: '"It has waited years. It can wait for my strength."',
                  react:
                    '"It can. It has the practice. The head is ruled; the line goes on waiting the way it knows how — open. Bring me the date when you are fit to put under it."',
                  flags: ['works-open-u2'],
                },
              ],
            },
          },
          'works-u3': {
            id: 'works-u3',
            voice: 'steward',
            speaker: 'genemon',
            motivates: [],
            greeting: [
              {
                voice: 'narrator',
                text: 'Genemon meets you at the kura door itself, the day-book carried out into the yard under his arm — a thing you have not seen him do in a year of watching him not do it.',
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: "\"You have stood the watch here. Then you know what I know: the count is honest and the walls are not equal to it. For eleven years this book has entered 'stored to the rafters' as a limit. It should have been a boast. One good harvest and the house's own rice would sleep in the weather.\"",
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"A second granary, then, raised beside the first. Timber, stone footing, board by board. The ground it stands on is the house\'s — that much costs you nothing. The rest is put in as it has been put in: your labour, your coin as you have it, entered as it goes. The term of the work is three words. Stores past need. This book has never once been able to write them."',
              },
            ],
            topics: [
              {
                id: 'works-u3-ask-a',
                label: '"Eleven years. Why now?"',
                answer: [
                  {
                    voice: 'steward',
                    speaker: NPC_NAME.genemon,
                    text: '"Because for eleven years the house was bleeding, and a bleeding house does not build. Look at the book, not at me: the lease\'s damage line, empty. The gate, the bunds, the roof — closed. The orchard — open and moving. A book that has stopped bleeding can afford to plan. The figures changed. Most of the hands in the changing are one hand\'s."',
                  },
                ],
              },
              {
                id: 'works-u3-ask-b',
                label: '"And if the harvests don\'t fill it?"',
                answer: [
                  {
                    voice: 'steward',
                    speaker: NPC_NAME.genemon,
                    text: '"Then it stands empty, and I enter an empty granary, which costs nothing to carry and shames no one but the weather. I have carried worse lines for poorer reasons. But I have footed this year twice, once for the house and once against my own doubt, and the two counts agree. It does not stand empty long."',
                  },
                ],
              },
            ],
            decision: {
              prompt: 'The measure of it is drawn on the page.',
              options: [
                {
                  id: 'works-u3-begin',
                  label: '"Raise it."',
                  say: '"Raise it."',
                  react:
                    '"Past winter\'s need. Opened, dated, against the kura wall for a desk. I will foot it against every season until it closes. See that the seasons are short."',
                  memory: [{ npc: 'genemon', warmthDelta: 1, regard: 'past-winters-need' }],
                  flags: ['works-open-u3'],
                },
                {
                  id: 'works-u3-hold',
                  label: '"Timber first. My back second. Then the book."',
                  say: '"Timber first. My back second. Then the book."',
                  react:
                    '"That is the whole craft of it in one line — I have known stewards who never learned it. The head is ruled. The timber will tell you when, and the book will still be here."',
                  flags: ['works-open-u3'],
                },
              ],
            },
          },
          'works-u4': {
            id: 'works-u4',
            voice: 'steward',
            speaker: 'genemon',
            motivates: [],
            greeting: [
              {
                voice: 'narrator',
                text: "The board, late, the lamp down to its last width of oil. The day-book lies open at the newest page — the second reckoning, ruled at the season's close: one line a season, in the book's plain words. Most of the page is still to come. Genemon sits before the empty part of it the way other men sit before a fire.",
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"You have looked at the omoya. From the forecourt — everyone looks from the forecourt; the house is arranged so that nobody sees further. The south rooms, shuttered since before your river. Nine years I have entered no repair against those rooms. Not because nothing wanted repairing."',
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"The second reckoning wants its capstone. Stores past need — rising. Ground taken back — taken. Walls that held — they held. One head remains, and in thirty-one years I have not had the standing to rule it. The house itself. Set in order, room by shut room, until this book can write of the omoya what it writes of the kura. Sound."',
              },
            ],
            topics: [
              {
                id: 'works-u4-ask-a',
                label: '"Nine years of silence on it. Why?"',
                answer: [
                  {
                    voice: 'steward',
                    speaker: NPC_NAME.genemon,
                    text: '"Because a repair entered is a repair owed. To write the omoya\'s wants into this book while the house could not answer them — that is not stewardship, that is accusation, one line a season, forever. So I kept the rooms shut and the page shut with them, and charged the silence to nobody. Both open now, or neither. The figures say now."',
                  },
                ],
              },
              {
                id: 'works-u4-ask-b',
                label: '"Why tell me?"',
                answer: [
                  {
                    voice: 'steward',
                    speaker: NPC_NAME.genemon,
                    text: '"Because the book says Gonbei against more closed lines than any name in it — count them yourself, the pages are numbered. When this house stands in order, the order will be made of entries, and most of the entries are yours. That is not thanks. It is arithmetic. Thanks is not my column."',
                  },
                  {
                    voice: 'narrator',
                    text: 'He says it to the page, not to you, and enters nothing, which from him is the same as looking up.',
                  },
                ],
              },
            ],
            decision: {
              prompt: 'The last head, unruled.',
              options: [
                {
                  id: 'works-u4-begin',
                  label: '"Rule it."',
                  say: '"Rule it."',
                  react:
                    '"The house, set in order. Opened. When it closes, this book\'s plain words will be: the estate stands. I have waited a working life to be allowed the sentence. See that I am here to enter it."',
                  memory: [{ npc: 'genemon', warmthDelta: 1, regard: 'the-house-stands' }],
                  flags: ['works-open-u4'],
                },
                {
                  id: 'works-u4-hold',
                  label: '"After the season\'s work. It can wait for me steady."',
                  say: '"After the season\'s work. It can wait for me steady."',
                  react:
                    '"It has waited longer, for worse reasons, under worse hands. The head is ruled and the page is patient — it is the one thing I own that is. Bring me the closing."',
                  flags: ['works-open-u4'],
                },
              ],
            },
          },
        },
        flavor: {
          worksNamedOrchard: `At the board, ${NAMES.elder} turns the day-book back years and reads one line aloud: orchard — open, never struck, waiting.`,
          worksNamedGranary:
            "The day-book rules a head it has never carried: a second granary — stores past the winter's need.",
          worksNamedHouse: `Under the second reckoning, ${NAMES.elder} rules the last head as plainly as any: the house itself, set in order.`,
          worksSeenGate:
            'The posts stand sound, the paint long gone, and the lower hinge cries on the swing exactly as entered. Four years of that sound, carried, and charged to no one.',
          worksSeenPaddies:
            "The bund's water-edge has slumped to a soft lip a thumb can push into, the mud below it fat with what six springs have taken. The figure said two hand-spans; the eye, measuring, gets two.",
          worksSeenWoodshed:
            'Daylight through the north slope, and a water-stain riding the rafter over your own mat. The book carries the damp against the house; the roof carries it against you.',
          worksSeenOrchard:
            'Wild rows to the shoulder, windfall gone to vinegar underfoot — and beneath the choke the trees stand in planted order, set out by someone who expected to walk here. The dens in the hollow are exactly where the losses said.',
          worksSeenKura:
            'The rice stands to the rafters with room for nothing above it — a limit the book has entered as a limit for eleven years. One good harvest would have nowhere to sleep.',
          worksSeenHouse:
            'From the forecourt the omoya shows its shut south face: shutters weathered grey into their grooves, rooms nine years absent from the book. Not one entry against any of it, and that is its own kind of entry.',
          worksLadderUnnamed: 'The day-book carries lines it has not read aloud yet.',
          worksLadderNamed: 'Named at the board. Now go and stand where the figures stand.',
          worksU1Label: 'Close the oldest lines',
          worksU1Blurb:
            'Gate, bunds, and the woodshed roof — three standing entries, mended as one work.',
          worksU1Done:
            "The hinge swings silent, the bunds hold their line, the woodshed roof turns the rain. At the board, three of the day-book's oldest open entries are ruled through in one evening — dated, footed, closed. (U1 · Stabilising)",
        },
      },
      {
        id: 'b',
        label: 'the household knows',
        brief:
          'the yard has worked around the damage for years; the book only ratifies what nobody says; each priced work binds the stray to the house',
        scorecard:
          "16✔ 5✘ (2 [briefed]: two-voice option reacts bend the one-react grammar; household speakers dilute Genemon's decide ownership (P3); warmest texture of the three)",
        scenes: {
          'works-intro': {
            id: 'works-intro',
            voice: 'steward',
            speaker: 'genemon',
            motivates: [],
            greeting: [
              {
                voice: 'narrator',
                text: 'Morning at the board, after rain. O-Hisa crosses the gateyard the long way, around a hollow that holds water — the same long way she took yesterday, and dry days too, the detour worn smoother than the reason for it. Rokusuke is in early, mud to the knee, saying nothing about where the mud came from. Nobody says anything. The house has its ways around its own sore places, and the ways stopped being noticed years ago.',
              },
              {
                voice: 'narrator',
                text: 'Genemon has the day-book open, and for once he is not writing. He is reading back — pages of it, a season at a time — with the face of a man counting a debt he has been letting stand.',
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"The gate-board is wedged. It has been wedged four winters. The wedge is in this book as kindling, which it is not."',
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"The east bund weeps. Rokusuke stakes it after every rain — his own straw, his own hour, no entry. The woodshed roof: the wood in there is stacked to miss a drip. Whoever stacks it knows where the drip falls. The book does not."',
              },
              { voice: 'narrator', text: 'He turns a page as if the page had argued with him.' },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"And the weir. The screens the house leases from Matsuzō — rats in them, coin owed across the water every season the gnawing runs ahead of the mending. The lease line has carried the damage so long that the carrying has started to look like the lease."',
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"Named, then — since the yard has plainly known it all along. The gate. The bund. The roof. The lease. Go and look at each with your own eyes; the book takes no man\'s word for damage, mine included. Then we will talk of work, and what work costs."',
              },
            ],
            topics: [
              {
                id: 'works-intro-ask-a',
                label: '"Why name it now?"',
                answer: [
                  {
                    voice: 'steward',
                    speaker: NPC_NAME.genemon,
                    text: '"Because the yard has been naming it for years — in wedges, in straw, in the long way round a puddle. A book that will not carry what the whole yard carries is not a record; it is a lie of omission. I keep worse things than losses out of this book. I will not keep that."',
                  },
                ],
              },
              {
                id: 'works-intro-ask-b',
                label: '"Whose coin mends it?"',
                answer: [
                  {
                    voice: 'steward',
                    speaker: NPC_NAME.genemon,
                    text: "\"Not the house's. The house's coin is spoken for before it reaches the yard — you were told so your first morning, and it has not changed. A man who puts his own coin and his own hours into a house is entered as having done so, and the book remembers longer than men do. What that is worth, ask me at a season's close.\"",
                  },
                ],
              },
            ],
            decision: {
              prompt: 'The concerns are named. What do you do with them?',
              options: [
                {
                  id: 'works-intro-go',
                  label: '"I\'ll walk them today, with my own eyes."',
                  say: '"I\'ll walk them today, with my own eyes."',
                  react:
                    '"Then take them in the book\'s order: gate, bund, roof, and the weir last — the water can be looked at while you are already wet from the bund. Say what you see when you have seen it. Looking is entered as work."',
                  memory: [{ npc: 'genemon', warmthDelta: 1, regard: 'walked-the-book' }],
                  flags: ['works-named-u1', 'works-named-weir'],
                },
                {
                  id: 'works-intro-hold',
                  label: '"The day\'s work first. I\'ll look as the round takes me."',
                  say: '"The day\'s work first. I\'ll look as the round takes me."',
                  react:
                    '"As the round takes you, then. The round goes past all four. It always has — that is rather the point."',
                  flags: ['works-named-u1', 'works-named-weir'],
                },
              ],
            },
          },
          'works-u1': {
            id: 'works-u1',
            voice: 'steward',
            speaker: 'genemon',
            motivates: [],
            greeting: [
              {
                voice: 'narrator',
                text: 'The board, evening. Word that you walked the named places has beaten you back to it — Shinnosuke watched you stare at the woodshed roof from the woodpile and told the kitchen, and the kitchen has told the pot: the bowl at the threshold is a little fuller tonight than the terms say. Genemon waits with the day-book open at a clean line.',
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"You have seen them. Say what you saw."',
              },
              {
                voice: 'player',
                text: '"The gate-board is soft behind the wedge. The east bund is Rokusuke\'s straw, not the house\'s earth. The roof shows daylight over my own mat."',
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"So the book has it from a hand that looked, and not from four winters of wedges. Board-wood, stone and straw, thatch. Small work, all of it, and all of it one lot — the kind that is never worth a day until it has cost a season."',
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"The house\'s coin is spoken for. If this is done, it is done on your own coin and your own hours, entered against your name. The book remembers what a man puts in. So, I find, does the yard."',
              },
            ],
            topics: [
              {
                id: 'works-u1-ask-a',
                label: '"Who set the wedge?"',
                answer: [
                  {
                    voice: 'steward',
                    speaker: NPC_NAME.genemon,
                    text: '"The hand before you, or the one before him. It passed to whoever slept nearest — things do, here, when the book is silent. Nobody was ever asked; nobody ever entered it. That is the habit I mean to end, starting with the wedge."',
                  },
                ],
              },
              {
                id: 'works-u1-ask-b',
                label: '"Why one lot?"',
                answer: [
                  {
                    voice: 'steward',
                    speaker: NPC_NAME.genemon,
                    text: '"Because the yard treats them as one: three places it steps around in a single round. Mend one, and the round still bends. Mend all three, and the yard walks straight — and knows exactly who straightened it. That last part is not nothing, for a man in your position."',
                  },
                ],
              },
            ],
            decision: {
              prompt: 'Three works, one lot. The book waits on your word.',
              options: [
                {
                  id: 'works-u1-begin',
                  label: '"Set me to it."',
                  say: '"Set me to it."',
                  react:
                    '"Entered. Gate, bund, roof, against your name. Rokusuke will hear it read at the morning board; expect his stakes and straw stacked at the bund by evening, sorted, best to hand. That is how the yard says so."',
                  memory: [{ npc: 'genemon', warmthDelta: 1, regard: 'set-to-it' }],
                  flags: ['works-open-u1'],
                },
                {
                  id: 'works-u1-hold',
                  label: '"When my purse can stand it."',
                  say: '"When my purse can stand it."',
                  react:
                    '"Then it stands entered and unpaid, like much else in here. The wedge has held four winters; it will hold while you count. Not forever."',
                  flags: ['works-open-u1'],
                },
              ],
            },
          },
          'works-u2': {
            id: 'works-u2',
            voice: 'steward',
            speaker: 'genemon',
            motivates: [],
            greeting: [
              {
                voice: 'narrator',
                text: "The board again. On it, beside the day-book, sits a small dish of bought plums — O-Hisa's, set down this morning without one word said. Genemon has been looking at the dish longer than a dish wants looking at.",
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"Nine years the kitchen has bought what this house once grew. The entry is one small line each market: plums, so many mon. Set the nine years end to end and it is not a small line. The kitchen has known it. The stall has known it. The dish is O-Hisa\'s way of asking when the book will."',
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"You have seen the rows. Sound trees under the choke, and dens dug in the hollow. The cutting-back is long work; the dens are Kihei\'s kind of work, and yours. The house calls the place the dogs\' yard. I mean to strike that name."',
              },
            ],
            topics: [
              {
                id: 'works-u2-ask-a',
                label: '"Whose orchard was it?"',
                answer: [
                  {
                    voice: 'steward',
                    speaker: NPC_NAME.genemon,
                    text: '"The house\'s. Planted for a larger house than this one — you have seen how the rows are laid: for paths between, and room to walk them. Who walked them is an old page. The trees are this year\'s business, and they are alive, which is more than the old page can say."',
                  },
                ],
              },
              {
                id: 'works-u2-ask-b',
                label: '"And the dogs?"',
                answer: [
                  {
                    voice: 'steward',
                    speaker: NPC_NAME.genemon,
                    text: '"Kihei\'s word on them is one word, and it has kept the yard whole this long. The dens come first — there is no cutting bramble over a den. Go to him before you go near the hollow, and go in his order. He will have one."',
                  },
                ],
              },
            ],
            decision: {
              prompt: 'The dish is still on the board.',
              options: [
                {
                  id: 'works-u2-begin',
                  label: '"The house grows its own plums again."',
                  say: '"The house grows its own plums again."',
                  react:
                    '"Entered: orchard, to be taken back. The kitchen will hold you to it harder than this book will, and the book does not forget. Take the dish back as you go — empty. O-Hisa will know what it means."',
                  memory: [{ npc: 'genemon', warmthDelta: 1, regard: 'ground-taken-back' }],
                  flags: ['works-open-u2'],
                },
                {
                  id: 'works-u2-hold',
                  label: '"After the season\'s work. It has waited nine years."',
                  say: '"After the season\'s work. It has waited nine years."',
                  react:
                    '"So it has — and each year has cost more than the one before it. Entered all the same. The dish stays on the board until the line closes; that is O-Hisa\'s term, not mine, and I would not cross it either."',
                  flags: ['works-open-u2'],
                },
              ],
            },
          },
          'works-u3': {
            id: 'works-u3',
            voice: 'steward',
            speaker: 'genemon',
            motivates: [],
            greeting: [
              {
                voice: 'narrator',
                text: 'Genemon is at the kura when you come back to it, and for once he is not counting rice — he is counting wall. Rokusuke stands by with the look of a man whose private arrangement is about to be read aloud.',
              },
              { voice: 'steward', speaker: NPC_NAME.genemon, text: '"Show him."' },
              {
                voice: 'villager',
                speaker: NPC_NAME.rokusuke,
                text: '"Bottom bales go musty against the north wall, so they get turned. Week about, all winter. The marks on the beam say whose week. It\'s not in any book. It never seemed a thing for a book."',
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"A wall\'s worth of labour every winter, spent standing still. And last autumn three bales sat a week in the forecourt under oilcloth, because the floor was full. If the wild rows come back to bearing, the harvests grow, and this kura will not grow with them. A second granary, raised at its side. Board by board, on your coin, against your name."',
              },
            ],
            topics: [
              {
                id: 'works-u3-ask-a',
                label: '"Why was it never raised before?"',
                answer: [
                  {
                    voice: 'steward',
                    speaker: NPC_NAME.genemon,
                    text: '"It was to be. The year it was to be raised was a year this house could keep no one it did not have to keep, and after that year, nobody asked again. Rokusuke\'s beam-marks have done the asking since — quietly, week about, for longer than he will say. I have decided to hear them."',
                  },
                ],
              },
              {
                id: 'works-u3-ask-b',
                label: '"Where does it stand?"',
                answer: [
                  {
                    voice: 'steward',
                    speaker: NPC_NAME.genemon,
                    text: '"Against the kura\'s south side, on the dry ground the oilcloth found last autumn. The forecourt taught the house where a granary should stand. We build on the lesson; it was dearly enough bought."',
                  },
                ],
              },
            ],
            decision: {
              prompt: 'Rokusuke is waiting on the answer too.',
              options: [
                {
                  id: 'works-u3-begin',
                  label: '"Raise it. The turning ends this winter."',
                  say: '"Raise it. The turning ends this winter."',
                  react:
                    '"Entered: granary, second, at the kura. Stores past the winter\'s need — the book has wanted to write that line a long time. Rokusuke keeps the turning till your roof is on; he said so before you answered."',
                  memory: [{ npc: 'genemon', warmthDelta: 1, regard: 'past-winters-need' }],
                  flags: ['works-open-u3'],
                },
                {
                  id: 'works-u3-hold',
                  label: '"Boards cost. Let me count my purse first."',
                  say: '"Boards cost. Let me count my purse first."',
                  react:
                    '"Count, then. The beam-marks will keep your place. They have kept it this long."',
                  flags: ['works-open-u3'],
                },
              ],
            },
          },
          'works-u4': {
            id: 'works-u4',
            voice: 'steward',
            speaker: 'genemon',
            motivates: [],
            greeting: [
              {
                voice: 'narrator',
                text: "Evening, the forecourt, the omoya's shut screens going grey with the last light. Naoyuki is there before you — standing where you stood to look at them, which means he saw you looking, or was told.",
              },
              {
                voice: 'official',
                speaker: NPC_NAME.naoyuki,
                text: '"When I was a boy, those rooms —"',
              },
              {
                voice: 'narrator',
                text: 'The sentence stops, the way his sentences do, as if he has read it back and found it not worth the ink. He goes in along the veranda. Genemon comes out by the same boards, the day-book under his arm, and stands where Naoyuki stood.',
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"You have seen the shut rooms. Everyone has. The house walks past its own best rooms daily, by the shortest way, the way a man walks past a debt. And still the veranda edge is wiped clean along its whole length — you will have seen that too."',
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"Thirty-one years I have written the closing of this house, door by door. A room shut is one line, and cheap. Opening is dearer. I rule a second reckoning now — walls that held, ground taken back, stores past need — and that page wants the omoya on it, or the page is a lie of a new kind."',
              },
              {
                voice: 'steward',
                speaker: NPC_NAME.genemon,
                text: '"It is the last work, and the largest: your coin, your hours, against your name in this book. Set the house in order, and whatever else this book has been, it ends as the record of a house that stands."',
              },
            ],
            topics: [
              {
                id: 'works-u4-ask-a',
                label: '"Who keeps the veranda edge clean?"',
                answer: [
                  {
                    voice: 'steward',
                    speaker: NPC_NAME.genemon,
                    text: '"O-Hisa, when she thinks the yard is empty. Nobody set her to it, and she would not thank the book for noticing. Houses are kept alive by such hands long after they are shut, and none of it has ever been entered. That is the arrears I mean to clear."',
                  },
                ],
              },
              {
                id: 'works-u4-ask-b',
                label: '"Why me?"',
                answer: [
                  {
                    voice: 'steward',
                    speaker: NPC_NAME.genemon,
                    text: '"Because every work on this page went in against your name, and the yard watched each one done. When this house asks, someday, who set it in order, the book should answer with a name it can stand behind. Yours is the one written on the doing."',
                  },
                ],
              },
            ],
            decision: {
              prompt: 'The last page of the reckoning is ruled, and empty.',
              options: [
                {
                  id: 'works-u4-begin',
                  label: '"Open the rooms. Set the house in order."',
                  say: '"Open the rooms. Set the house in order."',
                  react:
                    '"Entered. And when it is done, I will write the line I have kept thirty-one years for. See that you make it true."',
                  memory: [{ npc: 'genemon', warmthDelta: 1, regard: 'the-house-stands' }],
                  flags: ['works-open-u4'],
                },
                {
                  id: 'works-u4-hold',
                  label: '"Order is a large word. Let me come at it slowly."',
                  say: '"Order is a large word. Let me come at it slowly."',
                  react:
                    '"Slowly is how the rooms were shut — one line at a time. Come at it however you like; it is entered, and the page will wait. Pages do."',
                  flags: ['works-open-u4'],
                },
              ],
            },
          },
        },
        flavor: {
          worksNamedOrchard: `The day-book opens a heading the kitchen has paid toward for nine years in bought plums: orchard, gone wild, to be taken back — ${NAMES.elder} enters it without looking up, as if it had always been there.`,
          worksNamedGranary:
            "Entered in the day-book: granary, second, to be raised at the kura — the line saying plainly what Rokusuke's beam-marks and last autumn's oilcloth have said for years.",
          worksNamedHouse: `Entered, in ${NAMES.elder}'s smallest hand: the omoya, to be set in order. Nobody at the board says anything, which is how this house says most things.`,
          worksSeenGate:
            "The gate-board hangs by grace of a wedge someone renews without being asked, and the wood behind it has gone soft as old bread. Yohei's stall always ropes to the same post — even trade knows which one to trust.",
          worksSeenPaddies:
            "The east bund weeps through a fist of stakes and straw — Rokusuke's work, redone after every rain, entered nowhere. The water it loses is the far rows' water, and the far rows show it.",
          worksSeenWoodshed:
            "The wood is stacked in a horseshoe around one bare patch of floor, and Shinnosuke's perch on the pile keeps to the dry end — even the boy's trespassing has learned this roof. Above the bare patch, daylight.",
          worksSeenOrchard:
            "Under the bramble the fruit trees stand alive in their rows while the kitchen buys plums at the stall. The dogs' paths through the choke are wider than the house's.",
          worksSeenKura:
            "The rice is stacked to the beams and turned week about so the bottom bales can breathe — Rokusuke's arithmetic, not the builder's. One good harvest more than this floor can hold, and the surplus sits out under oilcloth.",
          worksSeenHouse:
            "From the forecourt the omoya's shut rooms show as a row of weathered screens, and along the veranda edge the dust is wiped in one clean band — someone still keeps that much of it, and no one says who.",
          worksLadderUnnamed: 'The yard works around what nobody has named.',
          worksLadderNamed: 'The book has named its concerns; go and see them with your own eyes.',
          worksU1Label: 'Make the first repairs',
          worksU1Blurb:
            'Mend the gate, the bund, and the woodshed roof — the small work the yard has walked around for years.',
          worksU1Done:
            "The wedge comes out of the gate, the bund holds through a whole rain, and the woodshed roof goes dark where daylight showed. By week's end O-Hisa crosses the gateyard the short way, and the yard's rounds run straight for the first time in four winters. (U1 · Stabilising)",
        },
      },
    ],
  },
];
